/* ThermoAspen Heater System Engine v1.8.1
 * 1.8.1 (28 Oct 2026, speed): identical requests made during one build are answered once.
 *   resolveInstallation and inspectPlacement keep their answer until the current piece of work
 *   finishes (the cache empties itself straight after), keyed by the full request plus the
 *   Construction Specification state. A 3D construction build asked for the same installation
 *   12 times (about 1.7 s); it is now worked out once. Callers get their own copy of the answer,
 *   except the large read-only roofAccommodation/building parts, which are shared.
 *   The catalogue's sizing policies are read once per catalogue instead of copying the whole
 *   catalogue twice on every analysis.
 * Stage 2F — manufacturer-led heater/flue resolution and final installation validation.
 * Regional building-regulation logic is deliberately not applied in this version.
 *
 * Dependencies:
 *   ThermoAspenHeaterCatalogue >= 1.0.5
 *   ThermoAspenHeaterInstallationContract >= 1.0.0
 *   ThermoAspenHeaterRoofAccommodationV1 >= 1.0.0
 *
 * This engine is headless. It does not modify Designer state or render anything.
 */
(function(W){
'use strict';

var VERSION='1.8.1';
var STAGE='2F';
var COORD='ti-room-xyz-v1';
var UNITS='mm';

function clone(v){return v==null?v:JSON.parse(JSON.stringify(v))}
function obj(v){return !!v&&typeof v==='object'&&!Array.isArray(v)}
function arr(v){return Array.isArray(v)}
function num(v,f){v=Number(v);return isFinite(v)?v:(f==null?null:f)}
function positive(v){return typeof v==='number'&&isFinite(v)&&v>0}
function clamp(v,a,b){return Math.max(a,Math.min(b,v))}
function issue(a,code,path,message,data){var x={code:code,path:path,message:message};if(data!==undefined)x.data=data;a.push(x)}
function catalogue(){return W.ThermoAspenHeaterCatalogue||null}
function contract(){return W.ThermoAspenHeaterInstallationContract||null}
function runtime(){return W.ThermoAspenProjectRuntime||null}
function roofAccommodation(){return W.ThermoAspenHeaterRoofAccommodationV1||null}

/* Projects saved before catalogue 1.2.2 may carry old model ids; map them once, on a clone. */
function legacyIds(p){
  var c=W.ThermoAspenHeaterCatalogue,h=p&&p.heater,n;
  if(!c||typeof c.normaliseModelId!=='function'||!h)return p;
  ['preferredModelId','modelId'].forEach(function(k){if(typeof h[k]==='string'){n=c.normaliseModelId(h[k]);if(n!==h[k])h[k]=n}});
  if(h.resolved&&typeof h.resolved.modelId==='string'){n=c.normaliseModelId(h.resolved.modelId);if(n!==h.resolved.modelId)h.resolved.modelId=n}
  return p;
}
function unwrapProject(v){
  if(!v)return null;
  if(v.schema&&v.schema.name==='ThermoAspenProject'&&v.design)return legacyIds(clone(v.design));
  if(v.design&&v.design.room)return legacyIds(clone(v.design));
  if(v.room)return legacyIds(clone(v));
  return null;
}

function currentProject(){
  var r=runtime(),p;
  if(r&&typeof r.readCurrentProject==='function'){
    try{p=unwrapProject(r.readCurrentProject());if(p)return p}catch(e){}
  }
  var d=W.__ThermoAspenDesignerV5,dd=W.ThermoAspenSaunaDesignerData;
  if(d&&typeof d.getProjectState==='function'){
    try{p=unwrapProject(d.getProjectState());if(p)return p}catch(e){}
  }
  p=unwrapProject(dd&&dd.project);
  return p;
}

function openingList(project){
  var o=project&&project.openings||{},out=[];
  function add(x,key){
    if(!x||typeof x!=='object')return;
    if(Array.isArray(x)){x.forEach(function(v,i){add(v,key+'['+i+']')});return}
    if(x.frameWidthMm!=null||x.widthMm!=null||x.type){
      var y=clone(x);y.__key=key;out.push(y);return;
    }
    Object.keys(x).forEach(function(k){add(x[k],key?key+'.'+k:k)});
  }
  Object.keys(o).forEach(function(k){add(o[k],k)});
  return out;
}

function openingType(o){
  var t=String(o&&o.type||'').toLowerCase();
  if(t==='door'||t==='window')return t;
  var k=String(o&&o.__key||'').toLowerCase();
  if(k.indexOf('door')>=0)return 'door';
  if(k.indexOf('window')>=0)return 'window';
  return 'opening';
}

function openingAreaM2(o){
  if(!o||o.enabled===false)return 0;
  var w=num(o.frameWidthMm,num(o.widthMm,0)),h=num(o.frameHeightMm,num(o.heightMm,0));
  return Math.max(0,w)*Math.max(0,h)/1000000;
}

function inferredNonInsulatedArea(project,options,warnings){
  options=options||{};
  var list=openingList(project),windows=0,doors=0;
  // ThermoAspen canonical sizing policy: count the entire door opening and
  // every window, irrespective of glass fraction or a glazing:false flag.
  // Never accept a legacy door-glazing override for heater sizing.
  list.forEach(function(o){
    var t=openingType(o),a=openingAreaM2(o);
    if(t==='window')windows+=a;
    else if(t==='door')doors+=a;
  });
  var extra=Math.max(0,num(options.additionalNonInsulatedAreaM2,0));
  return{
    windowsM2:windows,
    doorGlazingM2:doors,
    doorOpeningM2:doors,
    tileUpstandM2:0,
    tiledFloorM2:0,
    additionalM2:extra,
    totalM2:windows+doors+extra,
    rule:'ThermoAspen sizing counts the full door opening and all windows; tile upstand and tiled floor are excluded.'
  };
}

function analyse(input){
  input=input||{};
  var errors=[],warnings=[],p=unwrapProject(input.project)||input.project||currentProject();
  if(!p||!obj(p.room)){
    issue(errors,'PROJECT_ROOM_MISSING','project.room','A valid current project room is required.');
    return{valid:false,errors:errors,warnings:warnings};
  }

  var L=num(p.room.lengthMm,0),Wd=num(p.room.widthMm,0),H=num(p.room.heightMm,0);
  if(!(L>0&&Wd>0&&H>0)){
    issue(errors,'PROJECT_ROOM_DIMENSIONS_INVALID','project.room','Room dimensions must be positive.');
    return{valid:false,errors:errors,warnings:warnings};
  }

  var c=catalogue(),policy=sizingPolicies(c);
  var hp=policy&&policy['harvia-wood-v1']||{nonInsulatedSurfaceAdditionM3PerM2:1.2,massiveLogVolumeMultiplier:1.5};

  var actual=L*Wd*H/1000000000;
  var surf=inferredNonInsulatedArea(p,input,warnings);
  var surfaceAddition=surf.totalM2*num(hp.nonInsulatedSurfaceAdditionM3PerM2,1.2);
  var preLog=actual+surfaceAddition;
  var massiveLog=input.massiveLogWalls===true;
  var effective=massiveLog?preLog*num(hp.massiveLogVolumeMultiplier,1.5):preLog;

  return{
    valid:true,
    engineVersion:VERSION,
    sizingPolicyId:'harvia-wood-v1',
    room:{lengthMm:L,widthMm:Wd,heightMm:H,actualVolumeM3:actual},
    nonInsulatedArea:surf,
    surfaceAdditionM3:surfaceAddition,
    massiveLogWalls:massiveLog,
    massiveLogMultiplier:massiveLog?num(hp.massiveLogVolumeMultiplier,1.5):1,
    effectiveVolumeM3:effective,
    errors:errors,
    warnings:warnings,
    project:clone(p)
  };
}

function modelCandidates(input){
  var a=analyse(input),errors=a.errors.slice(),warnings=a.warnings.slice(),c=catalogue();
  if(!a.valid||!c)return{valid:false,analysis:a,candidates:[],errors:errors,warnings:warnings};
  if(!c){
    issue(errors,'HEATER_CATALOGUE_MISSING','environment','Heater Catalogue is not loaded.');
    return{valid:false,analysis:a,candidates:[],errors:errors,warnings:warnings};
  }

  // Automatic eligibility limits automatic discovery, not a customer's explicit
  // selection in the chart. Consider their preferred model separately, but
  // NEVER exempt it from volume/height/installation and clearance checks.
  var models=(c.listAutomaticModels?c.listAutomaticModels():c.listModels()).filter(function(m){return m&&m.identity&&m.identity.fuel==='wood'});
  var h=Object.assign({},a.project&&a.project.heater||{},input&&input.heaterIntent||{}),
      preferredId=h.preferredModelId||(h.selectionMode==='manual'&&h.modelId?h.modelId:null),
      preferred=preferredId&&c.getModel?c.getModel(preferredId):null;
  if(preferred&&preferred.identity&&preferred.identity.fuel==='wood'&&
     !models.some(function(m){return m.id===preferred.id}))models.push(preferred);
  var v=a.effectiveVolumeM3,H=a.room.heightMm;

  var all=models.map(function(m){
    var s=m.sizing||{},min=num(s.saunaVolumeMinM3,0),max=num(s.saunaVolumeMaxM3,0),mh=num(s.minimumRoomHeightMm,0);
    var volumeOk=v>=min&&v<=max,heightOk=H>=mh;
    return{
      modelId:m.id,
      manufacturer:m.identity&&m.identity.manufacturer||'Harvia',
      model:m.identity&&m.identity.model||m.id,
      priority:num(m.selection&&m.selection.priority,9999),
      volumeRangeM3:{min:min,max:max},
      minimumRoomHeightMm:mh,
      effectiveVolumeM3:v,
      headroomM3:max-v,
      volumeOk:volumeOk,
      heightOk:heightOk,
      suitable:volumeOk&&heightOk
    };
  });

  var suitable=all.filter(function(x){return x.suitable}).sort(function(a,b){
    if(a.priority!==b.priority)return a.priority-b.priority;
    if(a.volumeRangeM3.max!==b.volumeRangeM3.max)return a.volumeRangeM3.max-b.volumeRangeM3.max;
    return a.model.localeCompare(b.model);
  });

  if(!suitable.length){
    issue(errors,'NO_SUITABLE_HARVIA_MODEL','selection',
      'No current automatic-eligible Harvia wood stove covers the calculated effective sauna volume and room height.',
      {effectiveVolumeM3:v,roomHeightMm:H});
  }

  return{
    valid:errors.length===0,
    analysis:a,
    candidates:all,
    suitableCandidates:suitable,
    errors:errors,
    warnings:warnings
  };
}

function normaliseHeaterIntent(project,input){
  var h=project&&project.heater||{},defaults=contract()&&contract().makeDefaultProjectHeaterDesign?
    contract().makeDefaultProjectHeaterDesign():
    {id:'heater.01',fuel:'wood',selectionMode:'auto',modelId:null,placement:{wall:'front',offsetMm:300,rotationDeg:0},protection:{mode:'auto',configurationId:null},floor:{mode:'auto',systemId:null},flue:{outletPreference:'auto',routePreference:'auto',flueSystemId:'harvia-whp1500'},singleWallProtection:{mode:'auto',componentId:null}};

  var out=clone(defaults);

  if(h.id)out.id=h.id;
  // A selected model is only a preference. Never lock the system to it.
  out.selectionMode='auto';out.modelId=null;
  out.preferredModelId=h.preferredModelId||(h.selectionMode==='manual'&&h.modelId?h.modelId:null);

  if(obj(h.placement)){
    if(h.placement.wall)out.placement.wall=h.placement.wall;
    if(h.placement.offsetMm!=null)out.placement.offsetMm=num(h.placement.offsetMm,out.placement.offsetMm);
    if(h.placement.rotationDeg!=null)out.placement.rotationDeg=num(h.placement.rotationDeg,0);
    // Optional exact rear standoff from finished internal wall cladding to heater body.
    // When absent, retain the existing automatic minimum-standoff behaviour.
    if(h.placement.wallGapMm!=null){var wg=Number(h.placement.wallGapMm);if(isFinite(wg)&&wg>=0)out.placement.wallGapMm=wg;}
    if(h.placement.wallStandoffMode==='unshielded'||h.placement.wallStandoffMode==='shielded')out.placement.wallStandoffMode=h.placement.wallStandoffMode;
  }

  // Manufacturer protection is automatic: it is chosen only when needed for room fit.
  out.protection={mode:'auto',configurationId:null};
  if(obj(h.floor))out.floor=Object.assign(out.floor,h.floor);
  if(obj(h.flue))out.flue=Object.assign(out.flue,h.flue);
  if(obj(h.singleWallProtection))out.singleWallProtection=Object.assign(out.singleWallProtection||{},h.singleWallProtection);

  if(input&&input.heaterIntent){
    var hi=clone(input.heaterIntent);
    out=Object.assign(out,hi);
    if(obj(hi.singleWallProtection))out.singleWallProtection=Object.assign({},defaults.singleWallProtection||{},h.singleWallProtection||{},hi.singleWallProtection);
  }
  if(input&&obj(input.singleWallProtection))out.singleWallProtection=Object.assign({},out.singleWallProtection||{},clone(input.singleWallProtection));

  out.protection={mode:'auto',configurationId:null};
  return out;
}

/* The insulated (twin-wall) chimney starts above the stove shield and needs its own distance from combustible cladding.
 * With the flue centred at the top outlet, the stove-to-wall gap must therefore be at least
 *   casing clearance + casing radius - outlet distance from the rear face.
 * Harvia confirmed: 100 mm from the outer casing to combustible material. */
function twinWallMinimumRearGapMm(model){
  var c=catalogue(),sys=c&&typeof c.getFlueSystem==='function'?c.getFlueSystem('harvia-whp1500'):null,
      clr=num(sys&&sys.installationConstraints&&sys.installationConstraints.insulatedOuterCasingClearanceToCombustibleMm,100),
      od=num(sys&&sys.kit&&sys.kit.insulatedOuterDiameterMm,220),
      o=model&&model.flue&&model.flue.outlets&&model.flue.outlets.top,z=o&&o.enabled&&o.centreMm?num(o.centreMm.z,null):null;
  if(z==null)return 0;
  return Math.max(0,Math.ceil(clr+od/2-z));
}
function configClearances(model,config){
  var out;
  if(config&&config.effectiveClearancesFromHeaterBody&&config.effectiveClearancesFromHeaterBody.combustible)
    out=clone(config.effectiveClearancesFromHeaterBody.combustible);
  else out=clone(model.clearances.base.combustible);
  if(model&&model.identity&&model.identity.fuel==='wood'&&typeof out.rearMm==='number'){
    var need=twinWallMinimumRearGapMm(model);
    if(need>out.rearMm){out.publishedRearMm=out.rearMm;out.rearMm=need;out.rearLimitedBy='twin-wall-flue'}
  }
  return out;
}

function sideMapForWall(wall){
  if(wall==='back')return{low:'left',high:'right'};
  if(wall==='front')return{low:'right',high:'left'};
  if(wall==='left')return{low:'right',high:'left'};
  return{low:'left',high:'right'}; // right wall
}

/* v1.7.16 — manufacturer combustible-clearance parity for timber benches.
 * Harvia's published stove safety distances apply to combustible WALLS AND
 * BENCHES, unless that protection configuration has an explicitly different
 * bench rule. Do not require a redundant benchProtection flag when a verified
 * stove-attached sheath already publishes distances to combustible materials.
 * Unverified products never infer a reduction; verified protected faces use
 * their published combustible clearances. The 3D member/operational/door checks remain authoritative.
 */
function verifiedBenchClearances(model,cfg){
  var base=model&&model.clearances&&model.clearances.base&&model.clearances.base.combustible||{},
      result=clone(base),approved=[],items=cfg&&cfg.installedProtection||[],
      special=cfg&&cfg.benchProtection||null,c=catalogue();
  // An explicit manufacturer bench-specific limitation OVERRIDES general
  // combustible clearances. Never assume an unsupported bench reduction.
  if(special&&(special.applicability&&special.applicability.indexOf('timber-sauna-bench')<0||
       special.excluded===true||special.permitted===false||special.status==='not-approved'))
    return{clearances:result,verifiedFaces:approved,policy:'explicit-bench-exclusion'};

  // Include every catalogue-verified protected configuration. WL525/WL575
  // use 'verified-protected'; excluding that status silently reverted timber
  // BENCHES to unshielded values even while walls/SVG used 110 mm.
  var configPublished=!!cfg&&(cfg.status==='verified-clearance'||
      cfg.status==='verified-protected'||
      cfg.status==='generated-approved-panel-array');
  var systemsVerified=configPublished&&items.length>0&&items.every(function(item){
    if(!item||!item.systemId||!['left','right','rear','full'].includes(item.position))return false;
    var sys=c&&typeof c.getProtectionSystem==='function'?c.getProtectionSystem(item.systemId):null;
    var status=sys&&sys.verification&&sys.verification.status||'';
    if(!sys||status!=='verified-clearance-effect'&&status!=='manufacturer-published'&&status!=='published-protected-distances; installed-transform-incomplete')return false;
    if(!Array.isArray(sys.compatibleModelIds)||sys.compatibleModelIds.indexOf(model.id)<0)return false;
    if(Array.isArray(sys.permittedPositions)&&sys.permittedPositions.indexOf(item.position)<0)return false;
    return true;
  });
  // A verified stove-attached shield protects the documented COMBUSTIBLE
  // faces whether the surface is timber wall cladding or a timber bench.
  // Model protectionConfigurationMode names do not create a bench exemption.
  var approvedConfiguration=systemsVerified;
  if(special){
    // Explicit manufacturer bench clearances take priority over generic
    // catalogue combustible facts, even if they are MORE restrictive.
    approvedConfiguration=approvedConfiguration&&!!(special.verification&&
      special.verification.status==='manufacturer-published'&&
      Array.isArray(special.applicability)&&
      special.applicability.indexOf('timber-sauna-bench')>=0);
  }
  if(!approvedConfiguration)return{clearances:result,verifiedFaces:approved,
    policy:'unverified-bench-applicability'};

  var faceSet=[];
  items.forEach(function(item){
    var faces=item.position==='full'?['rear','left','right']:[item.position];
    faces.forEach(function(face){if(faceSet.indexOf(face)<0)faceSet.push(face)});
  });
  var published=cfg.effectiveClearancesFromHeaterBody&&
      cfg.effectiveClearancesFromHeaterBody.combustible||{};
  var explicit= special&&special.effectiveClearancesFromHeaterBody||null;
  faceSet.forEach(function(face){
    var field=face+'Mm',v=explicit&&explicit[field]!=null?explicit[field]:published[field];
    if(v==null||!isFinite(Number(v))||Number(v)<0||
       !isFinite(Number(base[field]))||Number(base[field])<0)return;
    // A protected face must never acquire less than the published distance.
    result[field]=Number(v);
    if(Number(v)<Number(base[field]))approved.push(face);
  });
  return{clearances:result,verifiedFaces:approved,
    policy:special?'manufacturer-bench-specific':'published-combustible-includes-benches'};
}
/* Height to which the installed stove-attached shielding really reaches (catalogue physical height).
 * Reduced (shielded) clearances are valid only up to this height; above it the unshielded distance applies. */
function shieldTopForConfig(cfg){
  var items=cfg&&cfg.installedProtection||[],c=catalogue(),top=null;
  items.forEach(function(it){
    var sys=c&&typeof c.getProtectionSystem==='function'?c.getProtectionSystem(it.systemId):null,
        h=num(sys&&sys.physical&&sys.physical.heightMm,null);
    if(h!=null)top=top==null?h:Math.min(top,h); // the lowest sheath limits the protected height
  });
  return top;
}
function fitForConfiguration(room,model,wall,config){
  var c=configClearances(model,config),map=sideMapForWall(wall);
  var width=num(model.physical.widthMm,0),depth=num(model.physical.depthMm,0);
  var along=(wall==='front'||wall==='back')?room.lengthMm:room.widthMm;
  var perpendicular=(wall==='front'||wall==='back')?room.widthMm:room.lengthMm;
  var low=num(c[map.low+'Mm'],0),high=num(c[map.high+'Mm'],0),rear=num(c.rearMm,0),front=num(c.frontMm,0);
  var alongRequired=low+width+high,perpRequired=rear+depth+front;
  var unverifiableFaces=['front','rear','left','right','above'].filter(function(face){
    return c[face+'Mm']==null || !isFinite(Number(c[face+'Mm']));
  });
  return{
    fits:along>=alongRequired&&perpendicular>=perpRequired,
    clearancesComplete:unverifiableFaces.length===0,
    unverifiedClearanceFaces:unverifiableFaces,
    wall:wall,
    sideMap:map,
    alongLengthMm:along,
    perpendicularLengthMm:perpendicular,
    required:{alongMm:alongRequired,perpendicularMm:perpRequired},
    clearances:c,
    benchProtection:verifiedBenchClearances(model,config),
    shieldTopMm:shieldTopForConfig(config),
    lowSideClearanceMm:low,
    highSideClearanceMm:high,
    minimumBodyOffsetMm:low,
    maximumBodyOffsetMm:along-width-high
  };
}

// For Legend stone-cage models, only an adjacent CLADDING wall may justify
// a side panel. Rear WL200 is independently eligible to reduce the wall gap.
function legendPanelsFaceOnlyWalls(model,room,wall,offset,cfg){
  if(model&&model.protectionConfigurationMode!=='legend-wl200-panel-array'&&model.protectionConfigurationMode!=='wall-only-panel-array')return true;
  var base=fitForConfiguration(room,model,wall,null),map=base.sideMap;
  var faces=(cfg&&cfg.installedProtection||[]).map(function(p){return p.position});
  var nearLow=offset!=null&&offset<base.minimumBodyOffsetMm-.01,
      nearHigh=offset!=null&&offset>base.maximumBodyOffsetMm+.01;
  return !faces.some(function(f){return(f===map.low&&!nearLow)||(f===map.high&&!nearHigh)});
}

function protectionOptions(model){
  var c=catalogue(),ids=model.compatibleProtectionConfigurationIds||[],out=[];
  ids.forEach(function(id){
    var cfg=c.getProtectionConfiguration&&c.getProtectionConfiguration(id);
    if(cfg)out.push(cfg);
  });

  if(model&&(model.protectionConfigurationMode==='legend-wl200-panel-array'||model.protectionConfigurationMode==='wall-only-panel-array')){
    var systemId=model.panelProtectionSystemId||'harvia-wl200';
    var base=clone(model.clearances.base.combustible),ps=c&&c.getProtectionSystem&&c.getProtectionSystem(systemId),
        eff=num(ps&&ps.clearanceEffect&&ps.clearanceEffect.protectedFaceEffectiveClearanceFromHeaterBodyMm,null),
        verified=!!(ps&&ps.verification&&ps.verification.status.indexOf('published')>=0)||systemId==='harvia-wl200',
        combos=[[],['left'],['rear'],['right'],['left','rear'],['rear','right'],['left','right'],['left','rear','right']];
    combos.forEach(function(faces){
      if(faces.length&&(!verified||eff==null))return;
      var clr=clone(base);
      faces.forEach(function(face){clr[face+'Mm']=eff});
      out.push({
        id:model.id+(faces.length?'-'+systemId+'-'+faces.join('-'):'-unshielded-generated'),
        modelId:model.id,
        status:faces.length?'generated-approved-panel-array':'generated-base',
        installedProtection:faces.map(function(face){return{systemId:systemId,position:face}}),
        effectiveClearancesFromHeaterBody:{combustible:clr},
        generatedFromProtectionSystemId:faces.length?systemId:null
      });
    });
  }

  // Unshielded catalogue base is always an eligible manufacturer configuration
  // when all base clearance facts are present, even if the catalogue lists only
  // optional accessory combinations for this model.
  var base=model&&model.clearances&&model.clearances.base&&model.clearances.base.combustible||{};
  if(out.length&&!out.some(function(cfg){return !(cfg.installedProtection||[]).length})&&
     ['left','right','rear','front','above'].every(function(k){return base[k+'Mm']!=null&&isFinite(Number(base[k+'Mm']))&&Number(base[k+'Mm'])>=0})){
    out.push({id:model.id+'-unshielded-base-generated',modelId:model.id,status:'generated-base',
      installedProtection:[],effectiveClearancesFromHeaterBody:{combustible:clone(base)}});
  }
  if(!out.length){
    out.push({
      id:model.id+'-unshielded-generated',
      modelId:model.id,
      status:'generated-base',
      installedProtection:[],
      effectiveClearancesFromHeaterBody:{combustible:clone(model.clearances.base.combustible)}
    });
  }

  var seen={},dedup=[];
  out.forEach(function(x){if(x&&x.id&&!seen[x.id]){seen[x.id]=true;dedup.push(x)}});
  return dedup;
}

// Layout obstacles are generated by the Floor Plan from the authoritative Bench
// Geometry members. They are NOT manufacturer clearance data and are never saved
// as project intent. The engine remains the only authority selecting shielding.
function layoutBox(a){
  if(!a)return null;
  if(a.aabbMm)a=a.aabbMm;
  var mn=a.min||a,max=a.max||a;
  var b={x1:num(mn.x,mn.x1),x2:num(max.x,max.x2),y1:num(mn.y,mn.y1),y2:num(max.y,max.y2),z1:num(mn.z,mn.z1),z2:num(max.z,max.z2)};
  return Object.keys(b).every(function(k){return typeof b[k]==='number'&&isFinite(b[k])})?b:null;
}
function layoutOverlap(a,b){
  return !!(a&&b&&a.x1<b.x2-.1&&a.x2>b.x1+.1&&a.y1<b.y2-.1&&a.y2>b.y1+.1&&a.z1<b.z2-.1&&a.z2>b.z1+.1);
}
// Build the entrance reservation from canonical project door geometry so that
// Construction/C&M resolve the SAME protection as the Floor Plan even when
// no SVG-specific layoutReservations argument is supplied. In the Floor Plan
// the active door editor may supply the same reservation explicitly; that takes
// precedence while an opening is being edited.
function projectEntranceReservation(input,room){
  var project=unwrapProject(input&&input.project)||null;
  if(!project)return null;
  var door=openingList(project).filter(function(o){return o.enabled!==false&&openingType(o)==='door'})[0];
  if(!door||['front','back','left','right'].indexOf(door.wall)<0)return null;
  var L=num(room.lengthMm,0),W=num(room.widthMm,0),H=num(room.heightMm,2100),
      area=L*W,
      clearDepth=area<3000000?500:area<4000000?600:area<5000000?700:area<6000000?800:900,
      across=door.wall==='front'||door.wall==='back'?L:W,
      width=num(door.frameWidthMm,num(door.widthMm,700)),
      span=Math.min(Math.max(0,width),across),
      depth=Math.min(clearDepth,door.wall==='front'||door.wall==='back'?W:L),
      start=clamp(num(door.offsetMm,0),0,Math.max(0,across-span));
  var box={x1:0,x2:L,y1:0,y2:H,z1:0,z2:W};
  if(door.wall==='front'){box.x1=start;box.x2=start+span;box.z1=W-depth}
  else if(door.wall==='back'){box.x1=start;box.x2=start+span;box.z2=depth}
  else if(door.wall==='left'){box.x2=depth;box.z1=start;box.z2=start+span}
  else{box.x1=L-depth;box.z1=start;box.z2=start+span}
  return{id:'entrance-clearance',aabbMm:box,type:'reserved-entrance-exclusion'};
}
// Explain AABB collisions using the actual stove-oriented manufacturer bench
// envelope. Distances are measured from the STOVE BODY, not wall cladding.
// This routine only annotates rejected obstacles and never alters the verdict.
function benchConflictMeasurement(geom,model,benchBox){
  var t=geom&&geom.transform,orig=t&&t.originMm,basis=t&&t.basis,
      dims=geom&&geom.benchClearanceEnvelope&&geom.benchClearanceEnvelope.distancesMm||{};
  if(!orig||!basis||!benchBox)return null;
  var localX=[],localZ=[];
  [benchBox.x1,benchBox.x2].forEach(function(wx){
    [benchBox.z1,benchBox.z2].forEach(function(wz){
      var dx=wx-orig.x,dz=wz-orig.z;
      localX.push(dx*basis.localX.x+dz*basis.localX.z);
      localZ.push(dx*basis.localZ.x+dz*basis.localZ.z);
    });
  });
  var left=Math.min.apply(null,localX),right=Math.max.apply(null,localX),
      rear=Math.min.apply(null,localZ),front=Math.max.apply(null,localZ),
      width=num(model&&model.physical&&model.physical.widthMm,0),
      depth=num(model&&model.physical&&model.physical.depthMm,0),
      measures=[{face:'left',availableMm:-right},{face:'right',availableMm:left-width},
        {face:'rear',availableMm:-front},{face:'front',availableMm:rear-depth}];
  var separated=measures.filter(function(q){return q.availableMm>=-.1});
  if(!separated.length)return{face:'body',availableMm:0,requiredMm:0,shortfallMm:null};
  // At a diagonal approach, use the limiting separation. This is a diagnostic
  // for the rectangular model; it does not pretend to measure a curved cage.
  separated.forEach(function(q){q.requiredMm=dims[q.face];q.shortfallMm=
    typeof q.requiredMm==='number'?Math.max(0,q.requiredMm-q.availableMm):null});
  separated.sort(function(a,b){
    return (a.shortfallMm==null?-1:a.shortfallMm)-(b.shortfallMm==null?-1:b.shortfallMm)
      || a.availableMm-b.availableMm;
  });
  return separated[0];
}
function formatPlacementConflict(q){
  if(!q)return null;
  if(q.type==='bench-manufacturer-clearance'){
    if(q.face==='body'||!isFinite(q.requiredMm)||!isFinite(q.availableMm))
      return 'Timber bench intersects the manufacturer bench-clearance envelope';
    var f=q.face&&q.face!=='body'?q.face+' side':'bench';
    return 'Timber bench ('+f+'): required '+Math.round(q.requiredMm)+' mm from stove body; '
      +'available '+Math.max(0,Math.round(q.availableMm))+' mm'
      +(q.shortfallMm!=null?'; short by '+Math.ceil(q.shortfallMm)+' mm':'');
  }
  if(/^shield-facing-bench-/.test(q.type))return 'Stove side shield faces a timber bench without manufacturer approval for bench-facing protection';
  if(q.type==='bench-clearance-unverified')return 'Timber bench clearance cannot be verified from the manufacturer catalogue';
  if(q.type==='stove-body-overlap')return 'Stove body overlaps a bench member';
  if(q.type==='firebox-operational-access')return 'Bench obstructs the firebox loading/access zone';
  if(q.type==='entrance-stove-body')return 'Stove body overlaps the entrance keep-clear zone';
  if(q.type==='combustible-clearance')return 'Combustible item intersects the manufacturer clearance zone';
  return q.type||'Unresolved clearance conflict';
}
function candidateLayoutStatus(model,room,wall,offset,fit,input,wallGapMm){
  var obstacles=input&&input.layoutObstacles||[],reserved=(input&&input.layoutReservations||[]).slice();
  if(!reserved.some(function(q){return q&&q.id==='entrance-clearance'})){
    var automaticDoor=projectEntranceReservation(input,room);
    if(automaticDoor)reserved.push(automaticDoor);
  }
  if(!obstacles.length&&!reserved.length)return{valid:true,conflicts:[]};
  var pg={wall:wall,resolvedOffsetMm:offset,fit:fit,room:room,wallGapMm:wallGapMm};
  var geom=stoveGeometry(model,pg),mount=model.identity&&model.identity.fuel==='electric'&&model.physical.installationLocation==='wall'?num(model.physical.minimumMountHeightMm,80):0;
  function raised(b){
    var a=layoutBox(b);if(!a)return null;
    a.y1+=mount;a.y2+=mount;return a;
  }
  var body=raised(geom.body.aabbMm);
  var safety=(geom.clearanceZones||[]).concat(geom.aboveShieldZones||[]).map(function(z){return raised(z.aabbMm)}).filter(Boolean);
  var planEnvelope=geom.clearanceEnvelope&&raised(geom.clearanceEnvelope.aabbMm);
  if(planEnvelope)safety.push(planEnvelope); // protected wall envelope
  var benchZone=geom.benchClearanceEnvelope;
  var benchAabb=benchZone&&raised(benchZone.aabbMm),
      benchUpper=benchZone&&benchZone.aboveShield&&raised(benchZone.aboveShield.aabbMm);
  var operational= model.identity&&model.identity.fuel==='wood'
    ?operationalAccessZones(model,pg,geom,[]).map(function(z){return raised(z.aabbMm)}).filter(Boolean):[];
  var conflicts=[];
  obstacles.forEach(function(o,i){
    var b=layoutBox(o),combustible=o&&o.combustible!==false;
    if(!b)return;
    var bodyConflict=layoutOverlap(body,b),accessConflict=operational.some(function(z){return layoutOverlap(z,b)});
    // Bench obstacles use the verified manufacturer combustible envelope.
    // Protection applies only to the actual shielded faces.
    var bench=combustible&&(o.kind!=='other'&&o.type!=='other');
    var unknownBench=bench&&(!benchZone||!benchZone.verified);
    var clearance=combustible&&(bench
      ?(unknownBench||layoutOverlap(benchAabb,b)||(benchUpper&&layoutOverlap(benchUpper,b)))
      :safety.some(function(z){return layoutOverlap(z,b)}));
    if(bodyConflict||accessConflict||clearance){
      var type=bodyConflict?'stove-body-overlap':accessConflict?'firebox-operational-access':
        unknownBench?'bench-clearance-unverified':bench?'bench-manufacturer-clearance':'combustible-clearance';
      var conflict={id:o.id||'obstacle.'+i,type:type};
      if(type==='bench-manufacturer-clearance'){
        var measured=benchConflictMeasurement(geom,model,b);
        if(measured)Object.keys(measured).forEach(function(k){conflict[k]=measured[k]});
      }
      conflicts.push(conflict);
    }
  });
  reserved.forEach(function(o,i){
    var b=layoutBox(o);
    if(!b)return;
    // Keep-clear ENTRANCE reservation is a physical exclusion, not a fire
    // clearance exclusion. Two transparent zones MAY overlap each other.
    // A manufacturer-approved side/rear shield may reduce the installed
    // wall standoff enough for the unchanged physical stove body to clear.
    if(layoutOverlap(body,b))
      conflicts.push({id:o.id||'reservation.'+i,type:'entrance-stove-body'});
  });
  return{valid:conflicts.length===0,conflicts:conflicts};
}
// A physical side sheath must not sit between a bench and the exposed stone
// side of a stove when it has NO verified combustible/bench applicability. The test uses actual three-dimensional bench members
// projected into stove-local coordinates. It is separate from the stricter
// verified bench-clearance check above. Unless explicit bench-facing manufacturer
// approval exists, a factory FULL sheath cannot be used to bypass adjacent
// timber bench clearances; an unshielded arrangement may remain available.
function shieldBetweenBenchAndStove(model,room,wall,offset,fit,cfg,input,wallGapMm){
  var items=cfg&&cfg.installedProtection||[],faces=[];
  items.forEach(function(item){
    if(item.position==='full'){faces.push('left','right')}
    else if(item.position==='left'||item.position==='right')faces.push(item.position);
  });
  if(!faces.length||!input||!arr(input.layoutObstacles)||!input.layoutObstacles.length)return [];
  // A manufacturer-verified single-piece FULL protective sheath covers the rear
  // and both sides as one certified accessory. Do not reject it merely because
  // a bench exists somewhere beside that side. candidateLayoutStatus separately
  // checks every actual timber member against the stove BODY, firebox-access
  // zone and verified timber-bench clearance envelope for its applied sheath.
  // This concession never applies to separately fitted Legend/stone-cage panels.
  var factoryFull=items.length===1&&items[0].position==='full'&&
      cfg.status==='verified-clearance'&&fit&&fit.clearancesComplete&&
      model.protectionConfigurationMode!=='legend-wl200-panel-array'&&
      model.protectionConfigurationMode!=='wall-only-panel-array';
  if(factoryFull)return [];
  var geom=stoveGeometry(model,{room:room,wall:wall,resolvedOffsetMm:offset,fit:fit,wallGapMm:wallGapMm}),
      permitted=fit&&fit.benchProtection&&fit.benchProtection.verifiedFaces||[],
      origin=geom.transform.originMm,basis=geom.transform.basis,
      w=num(model.physical.widthMm,0),d=num(model.physical.depthMm,0),conflicts=[];
  function localBounds(b){
    var x=[],z=[];
    [b.x1,b.x2].forEach(function(wx){[b.z1,b.z2].forEach(function(wz){
      var dx=wx-origin.x,dz=wz-origin.z;
      x.push(dx*basis.localX.x+dz*basis.localX.z);
      z.push(dx*basis.localZ.x+dz*basis.localZ.z);
    })});
    return{x1:Math.min.apply(null,x),x2:Math.max.apply(null,x),
      z1:Math.min.apply(null,z),z2:Math.max.apply(null,z)};
  }
  input.layoutObstacles.forEach(function(o,i){
    if(o&&o.combustible===false)return;
    var b=layoutBox(o);if(!b)return;
    var loc=localBounds(b);
    if(!(loc.z1<d-.1&&loc.z2>.1))return; // Not beside shielded side.
    if(faces.indexOf('left')>=0&&permitted.indexOf('left')<0&&loc.x2<=.1)
      conflicts.push({id:o.id||'bench.'+i,type:'shield-facing-bench-left'});
    if(faces.indexOf('right')>=0&&permitted.indexOf('right')<0&&loc.x1>=w-.1)
      conflicts.push({id:o.id||'bench.'+i,type:'shield-facing-bench-right'});
  });
  return conflicts;
}


/* Only two user-selectable rear wall positions. Derived from the current model's
 * approved configurations; side panels are NOT assumed to approve reduced bench
 * clearance. An absent manufacturer-approved rear protection means one choice.
 */
function rearShieldOf(cfg){
 return (cfg&&cfg.installedProtection||[]).some(function(p){return p.position==='rear'||p.position==='full'});
}
function rearStandoffValues(model,room,wall,offset){
 var base=model&&model.clearances&&model.clearances.base&&model.clearances.base.combustible||{},
     ordinary=base.rearMm==null?null:Number(base.rearMm),shielded=null;
 if(ordinary==null||!isFinite(ordinary)||ordinary<0||!['left','right','rear','front','above'].every(function(k){return base[k+'Mm']!=null&&isFinite(Number(base[k+'Mm']))&&Number(base[k+'Mm'])>=0}))return{valid:false,unshieldedMm:null,shieldedMm:null};
 protectionOptions(model).forEach(function(cfg){
   if(!rearShieldOf(cfg)||!legendPanelsFaceOnlyWalls(model,room,wall,offset,cfg))return;
   var f=fitForConfiguration(room,model,wall,cfg),r=num(f.clearances.rearMm,null);
   if(r==null||r<0||r>=ordinary-.01||!f.clearancesComplete)return;
   if(shielded==null||r<shielded)shielded=r;
 });
 return{valid:true,unshieldedMm:ordinary,shieldedMm:shielded};
}
function wallStandoffOptions(input){
 input=input||{};
 var project=unwrapProject(input.project)||{},cat=catalogue(),model=null,
     h=project.heater||{},id=input.modelId||h.modelId;
 if(!cat)return{valid:false,reason:'Heater catalogue is unavailable.'};
 if(id)model=cat.getModel(id);
 if(!model){
   var candidate=unifiedCandidates({project:project}),options=candidate&&candidate.suitableCandidates||[];
   if(options.length)model=cat.getModel(options[0].modelId);
 }
 if(!model)return{valid:false,reason:'No verified heater model is selected.'};
 var room=input.room||project.room;
 if(!room||!(room.lengthMm>0)||!(room.widthMm>0))return{valid:false,reason:'Room dimensions unavailable.'};
 var wall=input.wall||h.placement&&h.placement.wall||'front',offset=input.offsetMm;
 if(offset==null)offset=h.placement&&h.placement.offsetMm;
 var values=rearStandoffValues(model,room,wall,offset);
 return Object.assign({modelId:model.id,wall:wall},values);
}

/* Ceiling edge protection: preserve the last physical ceiling joist beside an
 * end/gable wall by moving an otherwise shielded stove INWARD only enough for
 * the existing 220 mm twin-wall + 100 mm combustible exclusion to clear it.
 * The heater's selected shielding remains unchanged.  If the actual structural
 * end-wall datum is unavailable, do not guess, and retain existing behaviour.
 * The existing Roof Accommodation module still performs all actual trimming.
 */
function requiredEndCeilingWallGap(model,room,wall,intent,input){
  var top=model&&model.flue&&model.flue.outlets&&model.flue.outlets.top,
      inset=num(top&&top.centreMm&&top.centreMm.z,null),
      f=intent&&intent.flue||{},rp=String(f.routePreference||'auto'),
      op=String(f.outletPreference||'auto');
  if(!top||top.enabled!==true||inset===null||inset<0||
     (rp!=='auto'&&rp!=='vertical-roof')||
     (op!=='auto'&&op!=='top'))return null;
  var project=unwrapProject(input&&input.project)||currentProject();
  if(!project)return null;
  var b=generateBuilding(project),v=b&&b.result;
  if(!b.valid||!v)return null;
  var roof=v.metrics&&v.metrics.roof||{},end=roof.endWalls||roof.gableWalls||[];
  if(roof.family!=='monopitch'||
     !Array.isArray(end)||end.indexOf(wall)<0)return null;
  var members=v.structuralMembers||[],plate=members.find(function(m){
    return m&&m.assembly&&m.assembly.wall===wall&&
      m.assembly.purpose==='bottom-plate'&&m.world&&m.world.aabbMm;
  });
  if(!plate)return null;
  var a=plate.world.aabbMm,width=44;
  // The current Construction Spec ceiling schedules have 44 mm wide members.
  // Do not infer an offset for a different/unrecognised joist width.
  var S=W.ThermoAspenConstructionSpecV1,rules;
  try{rules=S&&typeof S.getFramingRules==='function'&&S.getFramingRules(v.constructionSpec||project)}catch(e){}
  var secs=rules&&rules.ceiling&&rules.ceiling.joist&&rules.ceiling.joist.sectionsByClearSpan;
  if(Array.isArray(secs)&&secs.length){
    if(secs.some(function(t){return !(Number(t.widthMm)>0)||
      Math.abs(Number(t.widthMm)-Number(secs[0].widthMm))>.01}))return null;
    width=Number(secs[0].widthMm);
  }
  var face,finish;
  if(wall==='right'){face=Number(a.x1)-width;finish=Number(room.lengthMm)}
  else if(wall==='left'){face=Number(a.x2)+width;finish=0}
  else if(wall==='front'){face=Number(a.z1)-width;finish=Number(room.widthMm)}
  else if(wall==='back'){face=Number(a.z2)+width;finish=0}
  else return null;
  if(!isFinite(face)||!isFinite(finish))return null;
  // Inward distance of nearest physical ceiling joist edge from finished wall.
  var joistInset=(wall==='right'||wall==='front')?finish-face:face-finish;
  // Use the EXISTING selected manufacturer's twin-wall clearance authority.
  // No stove-shield clearance is ever transferred to the chimney.
  var sysId=f.flueSystemId||'harvia-whp1500',cat=catalogue(),
      system=cat&&cat.getFlueSystem&&cat.getFlueSystem(sysId);
  if(sysId!=='harvia-whp1500'||!system)return null;
  var od=num(system.kit&&system.kit.insulatedOuterDiameterMm,null),
      clear=num(system.installationConstraints&&system.installationConstraints.insulatedOuterCasingClearanceToCombustibleMm,null);
  if(!(od>0)||!(clear>=0))return null;
  var radius=od/2+clear,margin=3;
  var minimum=radius+margin+joistInset-inset;
  return isFinite(minimum)&&minimum>0?{
    minimumMm:minimum,joistInsetMm:joistInset,marginMm:margin,
    reason:'end-ceiling-joist',endWall:wall
  }:null;
}

function chooseProtectionAndPlacement(model,room,intent,warnings,input){
  input=input||{};
  var wall=intent.placement.wall||'front',mode=intent.protection&&intent.protection.mode||'auto';
  var c=catalogue(),opts=protectionOptions(model);
  if(mode==='none')opts=opts.filter(function(x){return !(x.installedProtection||[]).length});
  else if(mode==='manual'){
    var id=intent.protection.configurationId,q=id&&c.getProtectionConfiguration&&c.getProtectionConfiguration(id);
    opts=q?[q]:[];
  }
  var requestedOffset=num(intent.placement.offsetMm,null);
  var wallMode=intent.placement&&intent.placement.wallStandoffMode||null;
  if(wallMode!==null&&wallMode!=='shielded'&&wallMode!=='unshielded')return{valid:false,error:'INVALID_WALL_STANDOFF_MODE',conflicts:[]};
  var discrete=rearStandoffValues(model,room,wall,requestedOffset);
  if(wallMode==='shielded'&&discrete.shieldedMm==null)return{valid:false,error:'NO_APPROVED_REAR_SHIELD',conflicts:[]};
  var hasGap=intent.placement&&intent.placement.wallGapMm!=null,requestedGap=hasGap?Number(intent.placement.wallGapMm):null;
  var edge=(wallMode==='shielded'||wallMode==='unshielded'||!hasGap)?
    requiredEndCeilingWallGap(model,room,wall,intent,input):null;
  // Invalid explicit distances must never be interpreted as automatic clearance.
  if(hasGap&&(!isFinite(requestedGap)||requestedGap<0))return{valid:false,error:'INVALID_WALL_GAP',conflicts:[]};
  var evaluated=opts.map(function(cfg){
    var fit=fitForConfiguration(room,model,wall,cfg);
    if(!fit.fits||(wallMode&&!fit.clearancesComplete))return null;
    if(wallMode==='shielded'&&(!rearShieldOf(cfg)||Math.abs(fit.clearances.rearMm-discrete.shieldedMm)>.01))return null;
    if(wallMode==='unshielded'&&(rearShieldOf(cfg)||Math.abs(fit.clearances.rearMm-discrete.unshieldedMm)>.01))return null;
    var rearGap=wallMode==='shielded'?discrete.shieldedMm:
      wallMode==='unshielded'?discrete.unshieldedMm:
      hasGap?requestedGap:fit.clearances.rearMm;
    // The two USER drag choices are retained; the resolved physical distance
    // may be slightly larger to preserve the end ceiling joist.
    if(edge&&rearGap<edge.minimumMm)rearGap=Math.ceil(edge.minimumMm*100)/100;
    var perp=wall==='front'||wall==='back'?room.widthMm:room.lengthMm;
    if(!(rearGap>=fit.clearances.rearMm-.01)||rearGap+model.physical.depthMm+fit.clearances.frontMm>perp+.01)return null;
    if(!legendPanelsFaceOnlyWalls(model,room,wall,requestedOffset,cfg))return null;
    var inBounds=requestedOffset===null||(
      requestedOffset>=fit.minimumBodyOffsetMm-.01&&requestedOffset<=fit.maximumBodyOffsetMm+.01);
    if(input.strictPlacement&& !inBounds)return null;
    var offset=requestedOffset==null?fit.minimumBodyOffsetMm:
      clamp(requestedOffset,fit.minimumBodyOffsetMm,fit.maximumBodyOffsetMm);
    var layout=candidateLayoutStatus(model,room,wall,offset,fit,input,rearGap);
    var aestheticConflicts=shieldBetweenBenchAndStove(model,room,wall,offset,fit,cfg,input,rearGap);
    if(aestheticConflicts.length){
      layout.valid=false;layout.conflicts=layout.conflicts.concat(aestheticConflicts);
    }
    return{configuration:cfg,fit:fit,partCount:(cfg.installedProtection||[]).length,
      inBounds:inBounds,layout:layout,offsetMm:offset,wallGapMm:rearGap};
  }).filter(Boolean);
  // Only a PHYSICAL stove-body overlap disqualifies entrance clearance.
  // A stove fire-clearance rectangle may intersect door circulation.
  // Bench/fire risks continue to use verified manufacturer clearance data.
  var applicable=evaluated.filter(function(q){return q.layout.valid});
  applicable.sort(function(a,b){
    if(a.inBounds!==b.inBounds)return a.inBounds?-1:1;
    // A wall-protection panel is not an unnecessary aesthetic obstruction:
    // shorter approved REAR standoff is the priority. This permits the body
    // to sit closer to cladding and leaves the front/stone-cage faces open.
    // At an explicitly chosen wall distance, avoid fitting unnecessary shields.
    // Without a chosen distance preserve the original closer-to-wall priority.
    if((hasGap||wallMode)&&a.partCount!==b.partCount)return a.partCount-b.partCount;
    var ar=a.fit.clearances.rearMm,br=b.fit.clearances.rearMm;
    if(ar!==br)return ar-br;
    if(a.partCount!==b.partCount)return a.partCount-b.partCount;
    var ac=a.fit.required.alongMm*a.fit.required.perpendicularMm;
    var bc=b.fit.required.alongMm*b.fit.required.perpendicularMm;
    return ac-bc;
  });
  var chosen=applicable[0];
  // The 'unshielded' standoff mode fixes DISTANCE TO THE REAR WALL; it does
  // not prohibit certified SIDE protection. A one-sided accessory may cease to
  // fit when a stove approaches a second side wall. If no ordinary/unilateral
  // configuration is safe, try the model's MANUFACTURER-VERIFIED full sheath
  // at the SAME requested (unshielded) rear distance before rejecting the
  // selected model or substituting another heater. No perpendicular snap and
  // the same verified combustible/bench clearances apply to this fallback.
  if(!chosen&&wallMode==='unshielded'&&discrete.valid&&discrete.unshieldedMm!=null&&
     (!hasGap||Math.abs(requestedGap-discrete.unshieldedMm)<=.01)){
    var fullFallback=opts.filter(function(cfg){
      return cfg.status==='verified-clearance'&&
        (cfg.installedProtection||[]).length===1&&
        cfg.installedProtection[0].position==='full';
    }).map(function(cfg){
      var fit=fitForConfiguration(room,model,wall,cfg),gap=discrete.unshieldedMm;
      if(!fit.fits||!fit.clearancesComplete||!legendPanelsFaceOnlyWalls(model,room,wall,requestedOffset,cfg))return null;
      if(gap<fit.clearances.rearMm-.01||gap+model.physical.depthMm+fit.clearances.frontMm>
         (wall==='front'||wall==='back'?room.widthMm:room.lengthMm)+.01)return null;
      var inBounds=requestedOffset===null||
        (requestedOffset>=fit.minimumBodyOffsetMm-.01&&requestedOffset<=fit.maximumBodyOffsetMm+.01);
      if(input.strictPlacement&&!inBounds)return null;
      var offset=requestedOffset==null?fit.minimumBodyOffsetMm:
        clamp(requestedOffset,fit.minimumBodyOffsetMm,fit.maximumBodyOffsetMm);
      var layout=candidateLayoutStatus(model,room,wall,offset,fit,input,gap);
      var sheathConflict=shieldBetweenBenchAndStove(model,room,wall,offset,fit,cfg,input,gap);
      if(sheathConflict.length){layout.valid=false;layout.conflicts=layout.conflicts.concat(sheathConflict)}
      return{configuration:cfg,fit:fit,partCount:1,inBounds:inBounds,
        layout:layout,offsetMm:offset,wallGapMm:gap};
    }).filter(Boolean);
    evaluated=evaluated.concat(fullFallback);
    fullFallback=fullFallback.filter(function(q){return q.layout.valid});
    fullFallback.sort(function(a,b){return a.partCount-b.partCount||
      a.fit.required.alongMm-b.fit.required.alongMm});
    if(fullFallback.length)chosen=fullFallback[0];
  }
  if(!chosen){
    // Return distinct obstruction reasons, not the same door conflict repeated
    // once for every shield combination.
    var blocked=[],seen={};
    evaluated.forEach(function(q){(q.layout.conflicts||[]).forEach(function(conflict){
      var key=conflict.id+':'+conflict.type;
      if(!seen[key]){seen[key]=true;blocked.push(conflict)}
    })});
    return{valid:false,wall:wall,options:opts,evaluated:evaluated,
      error:evaluated.some(function(q){return !q.layout.valid})?'LAYOUT_OBSTRUCTED':
        input.strictPlacement?'EXACT_POSITION_NO_APPROVED_CONFIGURATION':'NO_PROTECTION_CONFIGURATION_FITS',
      conflicts:blocked};
  }
  var requested=requestedOffset==null?chosen.offsetMm:requestedOffset;
  var resolved=chosen.offsetMm;
  var selectedRearGap=wallMode==='shielded'?discrete.shieldedMm:
    wallMode==='unshielded'?discrete.unshieldedMm:
    hasGap?requestedGap:chosen.fit.clearances.rearMm;
  if(edge&&chosen.wallGapMm>selectedRearGap+.01)
    issue(warnings,'FLUE_END_CEILING_OFFSET','placement.wallGapMm',
      'Stove moved inward to clear the end ceiling joist.',
      {requestedMm:selectedRearGap,resolvedMm:chosen.wallGapMm,endWall:edge.endWall});
  if(Math.abs(resolved-requested)>.01)
    issue(warnings,'HEATER_OFFSET_ADJUSTED','placement.offsetMm',
      'Stove position adjusted to the nearest manufacturer-approved safety envelope.',
      {requestedMm:requested,resolvedMm:resolved});
  return{valid:true,wall:wall,configuration:chosen.configuration,fit:chosen.fit,
    requestedOffsetMm:requested,resolvedOffsetMm:resolved,wallGapMm:chosen.wallGapMm,wallStandoffMode:wallMode,
    requestedWallGapMm:selectedRearGap,flueEdgeOffsetApplied:!!(edge&&chosen.wallGapMm>selectedRearGap+.01),
    adjusted:Math.abs(resolved-requested)>.01,layoutValidation:chosen.layout};
}

function basisForWall(wall){
  if(wall==='back')return{localX:{x:1,y:0,z:0},localY:{x:0,y:1,z:0},localZ:{x:0,y:0,z:1}};
  if(wall==='front')return{localX:{x:-1,y:0,z:0},localY:{x:0,y:1,z:0},localZ:{x:0,y:0,z:-1}};
  if(wall==='left')return{localX:{x:0,y:0,z:-1},localY:{x:0,y:1,z:0},localZ:{x:1,y:0,z:0}};
  return{localX:{x:0,y:0,z:1},localY:{x:0,y:1,z:0},localZ:{x:-1,y:0,z:0}};
}

function originForPlacement(room,model,wall,offset,rearClearance){
  var w=model.physical.widthMm;
  if(wall==='back')return{x:offset,y:0,z:rearClearance};
  if(wall==='front')return{x:offset+w,y:0,z:room.widthMm-rearClearance};
  if(wall==='left')return{x:rearClearance,y:0,z:offset+w};
  return{x:room.lengthMm-rearClearance,y:0,z:offset};
}

function localToWorld(origin,basis,p){
  return{
    x:origin.x+basis.localX.x*p.x+basis.localY.x*p.y+basis.localZ.x*p.z,
    y:origin.y+basis.localX.y*p.x+basis.localY.y*p.y+basis.localZ.y*p.z,
    z:origin.z+basis.localX.z*p.x+basis.localY.z*p.y+basis.localZ.z*p.z
  };
}

function localBoxToAabb(origin,basis,b){
  var xs=[b.x1,b.x2],ys=[b.y1,b.y2],zs=[b.z1,b.z2],pts=[];
  xs.forEach(function(x){ys.forEach(function(y){zs.forEach(function(z){pts.push(localToWorld(origin,basis,{x:x,y:y,z:z}))})})});
  return{
    min:{x:Math.min.apply(null,pts.map(function(p){return p.x})),y:Math.min.apply(null,pts.map(function(p){return p.y})),z:Math.min.apply(null,pts.map(function(p){return p.z}))},
    max:{x:Math.max.apply(null,pts.map(function(p){return p.x})),y:Math.max.apply(null,pts.map(function(p){return p.y})),z:Math.max.apply(null,pts.map(function(p){return p.z}))}
  };
}

function stoveGeometry(model,placement){
  var c=placement.fit.clearances,w=model.physical.widthMm,d=model.physical.depthMm,h=model.physical.heightMm;
  var room=placement.room,wall=placement.wall,off=placement.resolvedOffsetMm,
      rear=placement.wallGapMm==null?c.rearMm:num(placement.wallGapMm,c.rearMm);
  var basis=basisForWall(wall),origin=originForPlacement(room,model,wall,off,rear);
  var body=localBoxToAabb(origin,basis,{x1:0,x2:w,y1:0,y2:h,z1:0,z2:d});

  // The PLAN safety reservation is the COMPLETE outer rectangle, including
  // all four corner quadrants. Four individual face strips remain available
  // for labelled distances, and the above-body vertical zone remains distinct.
  // Missing manufacturer dimensions are NOT zero-valued approvals: the
  // provisional geometry is marked as such and may only be used for editing.
  var unknownFaces=['left','right','rear','front'].filter(function(face){
    return typeof c[face+'Mm']!=='number'||!isFinite(c[face+'Mm']);
  });
  var faceDistance=function(face){var q=c[face+'Mm'];return typeof q==='number'&&isFinite(q)?q:0};
  var envelope={
    id:'heater.01.clearance.plan-envelope',face:'envelope',
    type:'stove-combustible-clearance',
    geometryKind:'complete-plan-rectangle',
    distancesMm:{left:c.leftMm,right:c.rightMm,rear:c.rearMm,front:c.frontMm},
    verified:unknownFaces.length===0,unverifiedFaces:unknownFaces,
    aabbMm:localBoxToAabb(origin,basis,{
      x1:-faceDistance('left'),x2:w+faceDistance('right'),
      y1:0,y2:h,z1:-faceDistance('rear'),z2:d+faceDistance('front')
    })
  };
  // Timber benches use the active manufacturer's COMBUSTIBLE clearances when
  // the actual installed protection is verified for those faces. Any explicit
  // bench-specific restrictions override that rule; unverified shields fail safe.
  var benchFact=placement.fit&&placement.fit.benchProtection||{},
      benchDistances=benchFact.clearances||{},
      baseCombustible=model.clearances&&model.clearances.base&&model.clearances.base.combustible||{},
      allBenchFaces=['left','right','rear','front'],
      unverifiedBenchFaces=allBenchFaces.filter(function(face){
        return typeof baseCombustible[face+'Mm']!=='number'||!isFinite(baseCombustible[face+'Mm']);
      });
  // Shielded distances hold only up to the top of the installed sheath. Above it the
  // manufacturer's UNSHIELDED combustible distances apply to anything combustible.
  var shieldTop=num(placement.fit&&placement.fit.shieldTopMm,null),
      baseC=baseCombustible,
      shieldedFace=function(face){return typeof baseC[face+'Mm']==='number'&&typeof benchDistances[face+'Mm']==='number'&&benchDistances[face+'Mm']<baseC[face+'Mm']-0.5},
      anyShielded=['left','right','rear','front'].some(shieldedFace),
      splitAt=(shieldTop!=null&&anyShielded&&shieldTop<h+num(baseC.aboveMm,0))?Math.max(0,shieldTop):null,
      upperBench=null;
  if(splitAt!=null){
    upperBench={shieldTopMm:splitAt,distancesMm:{left:num(baseC.leftMm,0),right:num(baseC.rightMm,0),rear:num(baseC.rearMm,0),front:num(baseC.frontMm,0)},
      aabbMm:localBoxToAabb(origin,basis,{x1:-num(baseC.leftMm,0),x2:w+num(baseC.rightMm,0),y1:splitAt,y2:h+num(baseC.aboveMm,0),z1:-num(baseC.rearMm,0),z2:d+num(baseC.frontMm,0)})};
  }
  var benchClearanceEnvelope={
    id:'heater.01.clearance.bench-verified-envelope',
    shieldTopMm:splitAt,aboveShield:upperBench,
    type:'stove-bench-exclusion',geometryKind:'complete-plan-rectangle',
    policy:benchFact.policy||'base-manufacturer-combustible-clearance',
    manufacturerVerifiedProtectedFaces:clone(benchFact.verifiedFaces||[]),
    distancesMm:{left:benchDistances.leftMm,right:benchDistances.rightMm,
      rear:benchDistances.rearMm,front:benchDistances.frontMm},
    verified:unverifiedBenchFaces.length===0,unverifiedFaces:unverifiedBenchFaces,
    aabbMm:localBoxToAabb(origin,basis,{
      x1:-num(benchDistances.leftMm,0),x2:w+num(benchDistances.rightMm,0),
      y1:0,y2:h+num(baseCombustible.aboveMm,0),z1:-num(benchDistances.rearMm,0),z2:d+num(benchDistances.frontMm,0)
    })
  };
  // Existing five face zones remain unchanged for downstream 3D and
  // construction consumers; the full plan rectangle is an additional named
  // geometry object, not a duplicate volume in the public zone collection.
  var zones=[
    {id:'heater.01.clearance.left',face:'left',type:'stove-combustible-clearance',distanceMm:c.leftMm,aabbMm:localBoxToAabb(origin,basis,{x1:-c.leftMm,x2:0,y1:0,y2:h,z1:0,z2:d})},
    {id:'heater.01.clearance.right',face:'right',type:'stove-combustible-clearance',distanceMm:c.rightMm,aabbMm:localBoxToAabb(origin,basis,{x1:w,x2:w+c.rightMm,y1:0,y2:h,z1:0,z2:d})},
    {id:'heater.01.clearance.rear',face:'rear',type:'stove-combustible-clearance',distanceMm:c.rearMm,aabbMm:localBoxToAabb(origin,basis,{x1:0,x2:w,y1:0,y2:h,z1:-c.rearMm,z2:0})},
    {id:'heater.01.clearance.front',face:'front',type:'stove-combustible-clearance',distanceMm:c.frontMm,aabbMm:localBoxToAabb(origin,basis,{x1:0,x2:w,y1:0,y2:h,z1:d,z2:d+c.frontMm})},
    {id:'heater.01.clearance.above',face:'above',type:'stove-combustible-clearance',distanceMm:c.aboveMm,aabbMm:localBoxToAabb(origin,basis,{x1:0,x2:w,y1:h,y2:h+c.aboveMm,z1:0,z2:d})}
  ];

  // Upper zones: the unshielded distances on the shielded faces, from the sheath top up to the stove top.
  var upperZones=[];
  if(splitAt!=null&&splitAt<h){
    var uz=function(face,box){upperZones.push({id:'heater.01.clearance.'+face+'.above-shield',face:face,type:'stove-combustible-clearance-above-shield',shieldTopMm:splitAt,distanceMm:num(baseC[face+'Mm'],0),aabbMm:localBoxToAabb(origin,basis,box)})};
    if(shieldedFace('left'))uz('left',{x1:-num(baseC.leftMm,0),x2:0,y1:splitAt,y2:h,z1:0,z2:d});
    if(shieldedFace('right'))uz('right',{x1:w,x2:w+num(baseC.rightMm,0),y1:splitAt,y2:h,z1:0,z2:d});
    if(shieldedFace('rear'))uz('rear',{x1:0,x2:w,y1:splitAt,y2:h,z1:-num(baseC.rearMm,0),z2:0});
    if(shieldedFace('front'))uz('front',{x1:0,x2:w,y1:splitAt,y2:h,z1:d,z2:d+num(baseC.frontMm,0)});
  }
  zones.forEach(function(z){if(splitAt!=null&&z.face!=='above')z.shieldTopMm=splitAt});
  var outlets={};
  Object.keys(model.flue.outlets||{}).forEach(function(k){
    var o=model.flue.outlets[k];if(!o||!o.enabled)return;
    outlets[k]={
      centreMm:localToWorld(origin,basis,o.centreMm),
      axis:{
        x:basis.localX.x*o.axis.x+basis.localY.x*o.axis.y+basis.localZ.x*o.axis.z,
        y:basis.localX.y*o.axis.x+basis.localY.y*o.axis.y+basis.localZ.y*o.axis.z,
        z:basis.localX.z*o.axis.x+basis.localY.z*o.axis.y+basis.localZ.z*o.axis.z
      }
    };
  });

  return{
    coordinateSystemId:COORD,
    transform:{originMm:origin,basis:basis},
    body:{modelId:model.id,aabbMm:body,widthMm:w,depthMm:d,heightMm:h},
    wallGapMm:rear,
    flueOutlets:outlets,
    clearanceEnvelope:envelope,
    benchClearanceEnvelope:benchClearanceEnvelope,
    clearanceZones:zones,
    aboveShieldZones:upperZones,
    shieldTopMm:splitAt
  };
}

function operationalAccessZones(model,placement,geom,warnings){
  var spec=model&&model.operationalZones&&model.operationalZones.fireboxAccess||{},
      depth=num(spec.frontDepthMm,null),side=num(spec.sideAllowanceMm,null),
      source='catalogue-operational-zone',status=String(spec.status||'catalogued'),
      w=num(model&&model.physical&&model.physical.widthMm,0),
      d=num(model&&model.physical&&model.physical.depthMm,0),
      h=num(model&&model.physical&&model.physical.heightMm,0),
      tr=geom&&geom.transform||{},origin=tr.originMm,basis=tr.basis;

  if(!origin||!basis||!(w>0&&d>0&&h>0))return[];

  // Manufacturer combustible FRONT clearance is already enforced by the
  // complete stove and bench-clearance envelopes. Do not invent an additional
  // 'operational' zone from that same distance; it duplicates the SVG area and
  // can reject seating twice. Keep a separate operational zone ONLY where the
  // manufacturer actually specifies its independent front access dimensions.
  if(!(depth>0))return[];
  if(!(side>=0))side=0;

  return[{
    id:'heater.01.operational.firebox-access',
    type:'operational-access',
    purpose:'firebox-loading-and-ash-removal',
    status:status,
    source:source,
    frontDepthMm:depth,
    sideAllowanceMm:side,
    aabbMm:localBoxToAabb(origin,basis,{x1:-side,x2:w+side,y1:0,y2:h,z1:d,z2:d+depth}),
    note:source==='catalogue-operational-zone'
      ?'Keep the catalogued firebox loading/ash-removal access zone unobstructed.'
      :'Keep this conservative firebox loading/ash-removal zone unobstructed. It currently equals the manufacturer front combustible-clearance depth until a distinct Harvia operational-access dimension is catalogued.'
  }];
}

function chooseFloorSystem(model,intent,input,warnings){
  var c=catalogue(),mode=intent.floor&&intent.floor.mode||'auto';
  if(mode==='none')return{systemId:null,reason:'disabled-by-design'};
  if(mode==='manual'){
    var id=intent.floor.systemId;
    var x=id&&c.getFloorSystem&&c.getFloorSystem(id);
    return x?{systemId:id,system:x,reason:'manual'}:{systemId:null,error:'FLOOR_SYSTEM_UNKNOWN'};
  }
  if(input&&input.floorRequiresProtection===false)return{systemId:null,reason:'floor-explicitly-does-not-require-protection'};
  var ids=model.compatibleFloorSystemIds||[];
  if(ids.length&&c.getFloorSystem){
    var sys=c.getFloorSystem(ids[0]);
    if(sys){
      issue(warnings,'FLOOR_PROTECTION_ASSUMED','assembly.floor',
        'Floor construction is not yet classified by the Heater Engine; compatible Harvia floor protection is provisionally included.',
        {systemId:ids[0]});
      return{systemId:ids[0],system:sys,reason:'auto-provisional'};
    }
  }
  return{systemId:null,reason:'no-compatible-floor-system'};
}

function recommend(input){
  input=input||{};
  var mc=modelCandidates(input),errors=mc.errors.slice(),warnings=mc.warnings.slice(),c=catalogue();
  if(!mc.valid)return{valid:false,analysis:mc.analysis,candidates:mc.candidates,suitableCandidates:mc.suitableCandidates||[],errors:errors,warnings:warnings};

  var p=mc.analysis.project,intent=normaliseHeaterIntent(p,input),room=mc.analysis.room;
  var selected=null,resolution=null;

  // Put the user's preferred model first, but try every suitable model
  // if the preference becomes unsafe, too powerful or otherwise unsuitable.
  var candidates=mc.suitableCandidates.slice();
  if(intent.preferredModelId)candidates.sort(function(a,b){
    return (a.modelId===intent.preferredModelId?-1:0)-(b.modelId===intent.preferredModelId?-1:0);
  });
  for(var i=0;i<candidates.length;i++){
    var m=c.getModel(candidates[i].modelId),pr=chooseProtectionAndPlacement(m,room,intent,warnings,input);
    if(pr.valid){selected=m;resolution=pr;break}
  }
  if(!selected)issue(errors,'NO_SUITABLE_MODEL_FITS','selection','No suitable catalogue model has an approved shielding arrangement satisfying the room, bench and physical entrance-clearance checks at this position.');

  return{
    valid:errors.length===0&&!!selected,
    engineVersion:VERSION,
    stage:STAGE,
    analysis:mc.analysis,
    intent:intent,
    candidates:mc.candidates,
    suitableCandidates:mc.suitableCandidates,
    recommendation:selected?{
      modelId:selected.id,
      manufacturer:selected.identity.manufacturer,
      model:selected.identity.model,
      reason:intent.preferredModelId&&selected.id===intent.preferredModelId?'preferred-model-compliant':'automatic-suitable-model',
      protectionConfigurationId:resolution.configuration.id,
      requestedOffsetMm:resolution.requestedOffsetMm,
      resolvedOffsetMm:resolution.resolvedOffsetMm
    }:null,
    _selectedModel:selected,
    _placementResolution:resolution,
    errors:errors,
    warnings:warnings
  };
}

function resolveCore(input){
  input=input||{};
  var r=recommend(input),errors=r.errors.slice(),warnings=r.warnings.slice();
  if(!r.valid)return{valid:false,engineVersion:VERSION,stage:STAGE,analysis:r.analysis,recommendation:r.recommendation,errors:errors,warnings:warnings};

  var model=r._selectedModel,pr=r._placementResolution;
  pr.room=r.analysis.room;
  var geom=stoveGeometry(model,pr);
  var operationalZones=operationalAccessZones(model,pr,geom,warnings);
  var floor=chooseFloorSystem(model,r.intent,input,warnings);
  if(floor.error)issue(errors,floor.error,'assembly.floor','Selected floor protection system is unavailable.');

  var pc=pr.configuration,protectionItems=(pc.installedProtection||[]).map(function(x,i){
    var ps=catalogue().getProtectionSystem&&catalogue().getProtectionSystem(x.systemId);
    return{instanceId:'heater.01.protection.'+(i+1),systemId:x.systemId,position:x.position,system:ps||null};
  });

  var totalLoadKg=num(model.physical.netWeightKg,0)+num(model.physical.stoneCapacityKg,0);
  protectionItems.forEach(function(x){totalLoadKg+=num(x.system&&x.system.physical&&x.system.physical.netWeightKg,0)});
  totalLoadKg+=num(floor.system&&floor.system.physical&&floor.system.physical.netWeightKg,0);

  var buildingRequirements=[{
    id:'heater.01.requirement.floor-load-check',
    type:'floor-load-check',
    status:'required',
    priority:'heater-system',
    geometryRef:'heater.01.body',
    hostRef:'floor',
    affectedMemberIds:[],
    note:'Check local floor structure for resolved heater installation load of approximately '+Math.round(totalLoadKg*10)/10+' kg before adding any chimney load.'
  }];
  if(operationalZones.length){
    buildingRequirements.push({
      id:'heater.01.requirement.firebox-access',
      type:'reserve-clear-zone',
      status:'required',
      priority:'heater-system',
      geometryRef:operationalZones[0].id,
      hostRef:'interior-layout',
      affectedMemberIds:[],
      note:'Keep the resolved firebox loading and ash-removal access zone unobstructed.'
    });
  }

  return{
    valid:errors.length===0,
    engineVersion:VERSION,
    stage:STAGE,
    contractComplete:false,
    contractStatus:'stove-core-and-operational-access-resolved; flue routing/penetrations/component manifest included',
    catalogueVersion:catalogue().version,
    coordinateSystemId:COORD,
    units:UNITS,

    analysis:r.analysis,
    selection:{
      modelId:model.id,
      manufacturer:model.identity.manufacturer,
      model:model.identity.model,
      effectiveVolumeM3:r.analysis.effectiveVolumeM3,
      candidateModelIds:r.suitableCandidates.map(function(x){return x.modelId}),
      reason:r.recommendation.reason
    },

    assembly:{
      stove:{
        id:r.intent.id||'heater.01',
        modelId:model.id,
        wall:pr.wall,
        requestedOffsetMm:pr.requestedOffsetMm,
        resolvedOffsetMm:pr.resolvedOffsetMm,
        adjusted:pr.adjusted,wallGapMm:pr.wallGapMm,
        requestedWallGapMm:pr.requestedWallGapMm,
        flueEdgeOffsetApplied:pr.flueEdgeOffsetApplied===true,
        protectionConfigurationId:pc.id,
        geometry:geom
      },
      protection:{
        configurationId:pc.id,
        items:protectionItems
      },
      floor:floor
    },

    safety:{
      clearanceZones:geom.clearanceZones,
      operationalZones:operationalZones
    },

    buildingRequirements:buildingRequirements,

    metrics:{
      resolvedInstallationLoadKg:totalLoadKg,
      clearanceFit:clone(pr.fit)
    },

    errors:errors,
    warnings:warnings
  };
}


/* --------------------------------------------------------------------------
   Stage 2B — building-envelope aware flue routing.
   Building geometry is consumed as an envelope/structure authority.
   Heater/flue geometry retains priority; conflicts emit Building Requirements.
   -------------------------------------------------------------------------- */

function buildingEngine(){
  return W.ThermoAspenBuildingGeometryEngine||W.ThermoAspenBuildingEngine||null;
}

function generateBuilding(project){
  var e=buildingEngine();
  if(!e||typeof e.generate!=='function')return{available:false,result:null,error:'BUILDING_ENGINE_UNAVAILABLE'};
  try{
    var r=e.generate({project:clone(project)});
    return{available:true,result:r,valid:!!(r&&r.valid),errors:clone(r&&r.errors||[]),warnings:clone(r&&r.warnings||[])};
  }catch(err){
    return{available:true,result:null,valid:false,errors:['BUILDING_ENGINE_EXCEPTION: '+String(err&&err.message||err)],warnings:[]};
  }
}

function aabbOfWorld(w){
  if(!w)return null;
  var a=w.aabbMm||w;
  if(a&&num(a.x1,null)!=null&&num(a.x2,null)!=null&&num(a.y1,null)!=null&&num(a.y2,null)!=null&&num(a.z1,null)!=null&&num(a.z2,null)!=null)
    return{x1:num(a.x1),x2:num(a.x2),y1:num(a.y1),y2:num(a.y2),z1:num(a.z1),z2:num(a.z2)};
  if(a&&a.min&&a.max)
    return{x1:num(a.min.x),x2:num(a.max.x),y1:num(a.min.y),y2:num(a.max.y),z1:num(a.min.z),z2:num(a.max.z)};
  if(w.centerMm&&w.sizeMm){
    var c=w.centerMm,q=w.sizeMm;
    return{x1:num(c.x)-num(q.x)/2,x2:num(c.x)+num(q.x)/2,y1:num(c.y)-num(q.y)/2,y2:num(c.y)+num(q.y)/2,z1:num(c.z)-num(q.z)/2,z2:num(c.z)+num(q.z)/2};
  }
  return null;
}

function roofFinishQuads(building){
  var out=[];
  (building&&building.surfaces||[]).forEach(function(s){
    var r=s&&s.render||{},pts=r.pointsMm;
    if(!Array.isArray(pts)||pts.length<3)return;
    var role=String(r.role||s.role||'').toLowerCase(),purpose=String(s.purpose||'').toLowerCase(),id=String(s.id||'').toLowerCase();
    if(role==='roof-finish'||purpose.indexOf('roof')>=0&&purpose.indexOf('metal')>=0||id.indexOf('roof.metal')>=0){
      out.push({id:s.id||('roof-surface-'+out.length),points:pts.map(clone),source:s});
    }
  });
  return out;
}

function planeYAtXZ(points,x,z){
  if(!points||points.length<3)return null;
  var a=points[0],b=points[1],c=points[2],
      ux=b.x-a.x,uy=b.y-a.y,uz=b.z-a.z,
      vx=c.x-a.x,vy=c.y-a.y,vz=c.z-a.z,
      nx=uy*vz-uz*vy,ny=uz*vx-ux*vz,nz=ux*vy-uy*vx;
  if(Math.abs(ny)<1e-9)return null;
  return a.y-(nx*(x-a.x)+nz*(z-a.z))/ny;
}

function planeGradientXZ(points){
  if(!points||points.length<3)return null;
  var a=points[0],b=points[1],c=points[2],
      ux=b.x-a.x,uy=b.y-a.y,uz=b.z-a.z,
      vx=c.x-a.x,vy=c.y-a.y,vz=c.z-a.z,
      nx=uy*vz-uz*vy,ny=uz*vx-ux*vz,nz=ux*vy-uy*vx;
  if(Math.abs(ny)<1e-9)return null;
  return{x:-nx/ny,z:-nz/ny};
}

function pointInPolyXZ(points,x,z){
  var inside=false,j=points.length-1;
  for(var i=0;i<points.length;i++){
    var xi=points[i].x,zi=points[i].z,xj=points[j].x,zj=points[j].z;
    var hit=((zi>z)!==(zj>z))&&(x<(xj-xi)*(z-zi)/((zj-zi)||1e-12)+xi);
    if(hit)inside=!inside;
    j=i;
  }
  return inside;
}

function fallbackRoofY(building,x,z){
  var r=building&&building.metrics&&building.metrics.roof||null;
  if(!r)return null;

  if(r.family==='pitched-gable'){
    var span=r.spanAxis==='x'?x:z;
    if(!num(r.finishApexMm,null)&&num(r.theoreticalRafterApexMm,null)==null)return null;
    var apex=num(r.finishApexMm,num(r.theoreticalRafterApexMm));
    return apex-Math.abs(span-num(r.ridgeCoordMm,0))*Math.tan(num(r.pitchRad,0));
  }

  if(r.family==='monopitch'){
    var u=r.axis==='x'?x:z,low=r.rafterUnderside&&r.rafterUnderside.lowPoint||null;
    if(!low)return null;
    var y=num(low.yMm,0)+(u-num(low.axisCoordMm,0))*num(r.sign,1)*num(r.rafterUnderside.slopeRisePerRun,0);
    y+=num(r.rafterTop&&r.rafterTop.verticalGapFromUncutUndersideMm,0);
    y+=num(r.roofBuild&&r.roofBuild.roofFinishUndersideVerticalOffsetMm,0);
    var pitch=num(r.pitchRad,0),gauge=num(r.covering&&r.covering.gaugeMm,0);
    y+=gauge/Math.max(Math.cos(pitch),1e-9);
    return y;
  }

  return null;
}

function roofIntersectionVertical(building,x,z){
  var qs=roofFinishQuads(building),hits=[];
  qs.forEach(function(q){
    if(pointInPolyXZ(q.points,x,z)){
      var y=planeYAtXZ(q.points,x,z),grad=planeGradientXZ(q.points);
      if(num(y,null)!=null)hits.push({
        surfaceId:q.id,
        pointMm:{x:x,y:y,z:z},
        source:'roof-finish-surface',
        planeGradientXZ:grad,
        surfaceMaterialCode:q.source&&q.source.materialCode||null,
        surfacePurpose:q.source&&q.source.purpose||null,
        surfaceRole:q.source&&q.source.render&&q.source.render.role||q.source&&q.source.role||null
      });
    }
  });
  if(hits.length){
    hits.sort(function(a,b){return a.pointMm.y-b.pointMm.y});
    return hits[0];
  }
  var fy=fallbackRoofY(building,x,z),r=building&&building.metrics&&building.metrics.roof||null,
      pitch=Math.abs(num(r&&r.pitchRad,0)),axis=r&&r.family==='monopitch'?r.axis:r&&r.spanAxis,
      grad=null;
  if(axis==='x')grad={x:Math.tan(pitch)*num(r&&r.sign,1),z:0};
  else if(axis==='z')grad={x:0,z:Math.tan(pitch)*num(r&&r.sign,1)};
  return num(fy,null)!=null?{surfaceId:null,pointMm:{x:x,y:fy,z:z},source:'roof-metrics-fallback',planeGradientXZ:grad}:null;
}

function selectedProtectionTopY(core){
  var items=core&&core.assembly&&core.assembly.protection&&core.assembly.protection.items||[],top=null;
  items.forEach(function(x){
    var h=num(x.system&&x.system.physical&&x.system.physical.heightMm,null);
    if(h!=null)top=top==null?h:Math.max(top,h);
  });
  return top;
}

function chooseFlueOutlet(core,routeType,intent,errors){
  var outs=core&&core.assembly&&core.assembly.stove&&core.assembly.stove.geometry&&core.assembly.stove.geometry.flueOutlets||{};
  var pref=intent&&intent.flue&&intent.flue.outletPreference||'auto',key;
  if(routeType==='vertical-roof')key=pref==='rear'?'rear':'top';
  else key=pref==='top'?'top':'rear';

  if(routeType==='vertical-roof'&&key!=='top'){
    issue(errors,'FLUE_OUTLET_ROUTE_UNSUPPORTED','flue.outletPreference','Stage 2B vertical-roof routing currently requires the top stove outlet.');
    return null;
  }
  if(routeType==='rear-wall-vertical'&&key!=='rear'){
    issue(errors,'FLUE_OUTLET_ROUTE_UNSUPPORTED','flue.outletPreference','Stage 2B rear-wall routing requires the rear stove outlet.');
    return null;
  }
  if(!outs[key]){
    issue(errors,'FLUE_OUTLET_UNAVAILABLE','assembly.stove.geometry.flueOutlets.'+key,'Selected stove does not expose the required flue outlet.');
    return null;
  }
  return{key:key,centreMm:clone(outs[key].centreMm),axis:clone(outs[key].axis)};
}


function roofSlopeRiseAtRadius(roofHit,radius,building){
  var g=roofHit&&roofHit.planeGradientXZ;
  if(g&&isFinite(Number(g.x))&&isFinite(Number(g.z)))
    return Math.max(0,radius*Math.sqrt(num(g.x,0)*num(g.x,0)+num(g.z,0)*num(g.z,0)));
  var r=building&&building.metrics&&building.metrics.roof||null;
  return Math.max(0,radius*Math.tan(Math.abs(num(r&&r.pitchRad,0))));
}

/*
 * Manufacturer terminal-height planning.
 *
 * The rule is catalogue data (flue system installationConstraints.terminalHeightRule):
 * the terminal stands a base rise above the roof surface at the flue centreline,
 * plus an additional rise for every metre of plan distance from the ridge. A
 * monopitch roof has no ridge, so its high roof edge is used as the equivalent
 * reference line (a ThermoAspen equivalence, not manufacturer text).
 *
 * Total-height and unsupported above-roof limits are independent constraints.
 * No numeric defaults are substituted: a missing catalogue field raises
 * CATALOGUE_FIELD_MISSING. Regional rules are deferred until the Specifications
 * section exists and are not applied here.
 */

function flueSystemRequirements(system,errors){
  var ic=system&&system.installationConstraints||{},kit=system&&system.kit||{},rule=ic.terminalHeightRule||{},
      exts=Array.isArray(system&&system.extensions)?system.extensions:[],
      ext=function(id){return exts.find(function(x){return String(x&&x.id||'').toUpperCase()===id})||{}},
      e500=ext('WHP500'),e1000=ext('WHP1000'),
      spec=[
        ['installationConstraints.maximumTotalHeightMm',ic.maximumTotalHeightMm],
        ['installationConstraints.maximumSelfSupportingHeightMm',ic.maximumSelfSupportingHeightMm],
        ['installationConstraints.insulatedOuterCasingClearanceToCombustibleMm',ic.insulatedOuterCasingClearanceToCombustibleMm],
        ['installationConstraints.minimumVisibleInsulatedSectionBelowLeadThroughMm',ic.minimumVisibleInsulatedSectionBelowLeadThroughMm],
        ['installationConstraints.terminalHeightRule.baseRiseAboveRoofAtFlueCentrelineMm',rule.baseRiseAboveRoofAtFlueCentrelineMm],
        ['installationConstraints.terminalHeightRule.additionalRisePerMetreFromReferenceMm',rule.additionalRisePerMetreFromReferenceMm],
        ['kit.insulatedModuleLengthMm',kit.insulatedModuleLengthMm],
        ['kit.includesNonInsulatedSmokePipeLengthMm',kit.includesNonInsulatedSmokePipeLengthMm],
        ['kit.insulatedOuterDiameterMm',kit.insulatedOuterDiameterMm],
        ['extensions[WHP500].lengthMm',e500.lengthMm],
        ['extensions[WHP500].insulatedLengthMm',e500.insulatedLengthMm],
        ['extensions[WHP1000].lengthMm',e1000.lengthMm],
        ['extensions[WHP1000].insulatedLengthMm',e1000.insulatedLengthMm]
      ],missing=[];
  spec.forEach(function(p){if(!(typeof p[1]==='number'&&isFinite(p[1])))missing.push(p[0])});
  if(missing.length){
    issue(errors,'CATALOGUE_FIELD_MISSING','flue.system',
      'The selected flue system catalogue record is missing required manufacturer data ('+missing.join(', ')+'). No default value is substituted.',
      {systemId:system&&system.id||null,missing:missing});
    return null;
  }
  return{
    maxTotalHeightMm:ic.maximumTotalHeightMm,
    maxSelfSupportingHeightMm:ic.maximumSelfSupportingHeightMm,
    casingClearanceMm:ic.insulatedOuterCasingClearanceToCombustibleMm,
    minVisibleInsulatedMm:ic.minimumVisibleInsulatedSectionBelowLeadThroughMm,
    baseRiseMm:rule.baseRiseAboveRoofAtFlueCentrelineMm,
    perMetreMm:rule.additionalRisePerMetreFromReferenceMm,
    ruleId:rule.id||null,
    ruleVerificationStatus:rule.verificationStatus||null,
    moduleLengthMm:kit.insulatedModuleLengthMm,
    connectorLengthMm:kit.includesNonInsulatedSmokePipeLengthMm,
    outerDiameterMm:kit.insulatedOuterDiameterMm,
    inc500Mm:e500.insulatedLengthMm,inc1000Mm:e1000.insulatedLengthMm,
    nominal500Mm:e500.lengthMm,nominal1000Mm:e1000.lengthMm
  };
}
function terminalReference(building,roofHit){
  var r=building&&building.metrics&&building.metrics.roof||{},
      x=roofHit&&roofHit.pointMm&&num(roofHit.pointMm.x,null),
      z=roofHit&&roofHit.pointMm&&num(roofHit.pointMm.z,null),
      family=String(r&&r.family||''),coord,ref;

  if(family==='pitched-gable'){
    coord=r.spanAxis==='x'?x:z;
    ref=num(r.ridgeCoordMm,null);
    if(coord==null||ref==null)return null;
    return{
      roofFamily:family,
      referenceType:'ridge',
      referenceAxis:r.spanAxis==='x'?'x':'z',
      referenceCoordinateMm:ref,
      flueCoordinateMm:coord,
      planDistanceFromReferenceMm:Math.abs(coord-ref)
    };
  }

  if(family==='monopitch'){
    coord=r.axis==='x'?x:z;
    ref=num(r.highEaveRoofEdgeMm,null);
    if(ref==null)ref=num(r.rafterUnderside&&r.rafterUnderside.highPoint&&r.rafterUnderside.highPoint.axisCoordMm,null);
    if(coord==null||ref==null)return null;
    return{
      roofFamily:family,
      referenceType:'high-roof-edge',
      referenceAxis:r.axis==='x'?'x':'z',
      referenceCoordinateMm:ref,
      flueCoordinateMm:coord,
      planDistanceFromReferenceMm:Math.abs(coord-ref),
      note:'Monopitch uses the high roof edge as the equivalent of the ridge because this roof family has no ridge (ThermoAspen equivalence, not manufacturer text).'
    };
  }

  return null;
}

function manufacturerTerminalPlan(building,roofHit,outer,system,req){
  var outerRadius=outer/2,
      slopeRise=roofSlopeRiseAtRadius(roofHit,outerRadius,building),
      highestContactY=roofHit.pointMm.y+slopeRise,
      c=system&&system.installationConstraints||{},
      ref=terminalReference(building,roofHit),
      baseMm=req.baseRiseMm,perMetreMm=req.perMetreMm,
      distanceMm=ref?num(ref.planDistanceFromReferenceMm,0):0,
      ruleRiseMm=baseMm+(distanceMm/1000)*perMetreMm,
      ruleRequiredY=roofHit.pointMm.y+ruleRiseMm,
      physicalRequiredY=highestContactY+0.1,
      requiredY=Math.max(physicalRequiredY,ruleRequiredY);
  return{
    authority:'manufacturer',
    terminalHeightAuthority:'manufacturer-catalogue',
    manufacturer:system&&system.identity&&system.identity.manufacturer||'Harvia',
    systemId:system&&system.id||null,
    systemModel:system&&system.identity&&system.identity.model||null,
    roofIntersection:clone(roofHit),
    highestCasingRoofContactYmm:highestContactY,
    casingSlopeRiseMm:slopeRise,
    flueOuterDiameterMm:outer,
    minimumPhysicalTerminalYmm:physicalRequiredY,
    requiredTerminalYmm:requiredY,
    requirementBasis:'manufacturer-base-rise-plus-rise-per-m-from-ridge-or-high-roof-edge',
    terminalHeightRule:{
      ruleId:req.ruleId,
      status:'manufacturer-catalogue-rule',
      verificationStatus:req.ruleVerificationStatus,
      baseRiseAboveRoofAtFlueCentrelineMm:baseMm,
      additionalRisePerMetreFromReferenceMm:perMetreMm,
      roofReference:clone(ref),
      referenceResolved:!!ref,
      planDistanceFromReferenceMm:distanceMm,
      requiredRiseAboveRoofAtFlueCentrelineMm:ruleRiseMm,
      requiredTerminalYmm:ruleRequiredY
    },
    manufacturerConstraints:{
      orientation:c.orientation||null,
      maximumTotalHeightMm:req.maxTotalHeightMm,
      maximumSelfSupportingHeightMm:req.maxSelfSupportingHeightMm,
      insulatedOuterCasingClearanceToCombustibleMm:req.casingClearanceMm,
      minimumVisibleInsulatedSectionBelowLeadThroughMm:req.minVisibleInsulatedMm
    },
    regionalSpecification:{status:'deferred',applied:false},
    status:'manufacturer-plan'
  };
}

function chooseInsulatedModules(requiredLengthMm,req){
  var max=req.maxTotalHeightMm,
      base=req.moduleLengthMm,
      inc500=req.inc500Mm,
      inc1000=req.inc1000Mm,
      nominal500=req.nominal500Mm,
      nominal1000=req.nominal1000Mm,
      best=null;

  // Harvia states that extension sections increase the installed chimney height by
  // the length of their insulated component (WHP500 = 470 mm, WHP1000 = 970 mm),
  // not by their nominal overall 500/1000 mm product length.
  for(var n500=0;n500<=8;n500++){
    for(var n1000=0;n1000<=8;n1000++){
      var total=base+n500*inc500+n1000*inc1000;
      if(total+1e-6<requiredLengthMm||total>max+1e-6)continue;
      var pieces=1+n500+n1000,excess=total-requiredLengthMm;
      if(!best||excess<best.excessMm-1e-6||(Math.abs(excess-best.excessMm)<1e-6&&pieces<best.pieceCount)){
        best={
          totalLengthMm:total,
          excessMm:excess,
          pieceCount:pieces,
          lengthBasis:'manufacturer-installed-increment',
          modules:[
            {componentId:'harvia-whp1500-module',quantity:1,lengthMm:base,nominalLengthMm:base,installedIncrementMm:base},
            {componentId:'harvia-whp1000',quantity:n1000,lengthMm:nominal1000,nominalLengthMm:nominal1000,installedIncrementMm:inc1000},
            {componentId:'harvia-whp500',quantity:n500,lengthMm:nominal500,nominalLengthMm:nominal500,installedIncrementMm:inc500}
          ].filter(function(x){return x.quantity>0})
        };
      }
    }
  }
  return best;
}

function memberCylinderConflict(member,x,z,y0,y1,radius){
  var a=aabbOfWorld(member&&member.world||member&&member.render&&member.render.world||member&&member.aabbMm);
  if(!a)return false;
  var lo=Math.min(y0,y1),hi=Math.max(y0,y1);
  if(a.y2<lo||a.y1>hi)return false;
  var dx=x<a.x1?a.x1-x:(x>a.x2?x-a.x2:0),
      dz=z<a.z1?a.z1-z:(z>a.z2?z-a.z2:0);
  return dx*dx+dz*dz<=radius*radius;
}

function structuralConflicts(building,x,z,y0,y1,radius){
  var ids=[];
  (building&&building.structuralMembers||[]).forEach(function(m){
    if(memberCylinderConflict(m,x,z,y0,y1,radius))ids.push(m.id||null);
  });
  return ids.filter(function(x){return !!x});
}

function wallPlaneForRoom(room,wall){
  if(wall==='back')return{axis:'z',value:0,outward:{x:0,y:0,z:-1}};
  if(wall==='front')return{axis:'z',value:room.widthMm,outward:{x:0,y:0,z:1}};
  if(wall==='left')return{axis:'x',value:0,outward:{x:-1,y:0,z:0}};
  return{axis:'x',value:room.lengthMm,outward:{x:1,y:0,z:0}};
}

/* Flue strategy, in order of preference:
 *   1. single-wall flue (clear of combustible surfaces by the pipe's unprotected clearance),
 *   2. single-wall flue covered by the Harvia WZ020130 shield (reduced clearance, one shield only),
 *   3. insulated (twin-wall) chimney starting at the stove outlet (last resort).
 * The first strategy that keeps its clearances wins. A project that names its own protector is left exactly as specified. */
var FLUE_TWIN_WALL_STUB_MM=100;
function routeVerticalRoofAuto(core,building,system,intent,input,errors,warnings){
  var named=intent&&intent.singleWallProtection||input&&input.singleWallProtection;
  if(named&&named.componentId)return routeVerticalRoof(core,building,system,intent,input,errors,warnings,{selection:{tier:0,id:'user-specified',label:'Protector specified in the project'}});
  var c=catalogue(),room=core&&core.analysis&&core.analysis.room,windows=clearanceSurfaces(core),
      pipe=c&&c.getInstallationComponent&&c.getInstallationComponent('harvia-wz115100'),guard=c&&c.getInstallationComponent&&c.getInstallationComponent('harvia-wz020130'),
      required=num(pipe&&pipe.safety&&pipe.safety.combustibleRadialClearanceMm,500),
      gh=num(guard&&guard.physical&&guard.physical.heightMm,235),protectedClear=num(guard&&guard.safetyEffect&&guard.safetyEffect.protectedPipeClearanceMm,250),
      e1=[],w1=[],r1,e,w,r,cl,y0,seg;
  r1=routeVerticalRoof(core,building,system,intent,input,e1,w1,{selection:{tier:1,id:'single-wall',label:'Single-wall flue',detail:'Clear of combustible surfaces by at least '+required+' mm.'}});
  if(!r1||e1.length){e1.forEach(function(x){errors.push(x)});w1.forEach(function(x){warnings.push(x)});return r1}
  seg=r1.segments[0];cl=roomWallClearanceFromVerticalPipe(room,seg,windows);
  if(!cl||cl.nearestSurfaceClearanceMm>=required-0.1){w1.forEach(function(x){warnings.push(x)});return r1}
  y0=num(r1.outlet&&r1.outlet.centreMm&&r1.outlet.centreMm.y,0);
  /* 2. single-wall + Harvia shield. The shield covers ONE stretch (<= its height) that is closer than the
   * unprotected distance; single-wall continues above it until the next too-close stretch, where the insulated chimney takes over. */
  var fs=failStretches(cl,required),tY=y0+gh;
  if(fs.length){
    var firstLen=fs[0].y1+10-fs[0].y0;
    if(firstLen<=gh+0.1&&fs[0].min>=protectedClear-0.1)tY=fs.length>1?fs[1].y0:null;
  }
  e=[];w=[];
  r=routeVerticalRoof(core,building,system,intent,input,e,w,{transitionYmm:tY,protectionRequest:{mode:'manufacturer-guard',componentId:'harvia-wz020130'},
    selection:{tier:2,id:'single-wall-shielded',label:'Single-wall flue with shield',detail:'Closer than '+required+' mm to a combustible surface, so the single-wall pipe is covered by the Harvia WZ020130 shield (clearance '+protectedClear+' mm).'}});
  if(r&&!e.length){cl=roomWallClearanceFromVerticalPipe(room,r.segments[0],windows);
    if(cl&&cl.nearestSurfaceClearanceMm>=protectedClear-0.1){w.forEach(function(x){warnings.push(x)});return r}}
  /* 3. insulated chimney from the stove outlet */
  e=[];w=[];
  r=routeVerticalRoof(core,building,system,intent,input,e,w,{transitionYmm:y0+FLUE_TWIN_WALL_STUB_MM,
    selection:{tier:3,id:'twin-wall',label:'Insulated (twin-wall) chimney',detail:'Too close to combustible surfaces for single-wall pipe, even shielded, so the insulated chimney starts at the stove outlet.'}});
  if(r&&!e.length){
    var ins=r.segments[1],g=ins&&ins.geometry||{},casing=roomWallClearanceFromVerticalPipe(room,{geometry:{startMm:g.startMm,endMm:{x:num(g.startMm&&g.startMm.x,0),y:num(room&&room.heightMm,2100),z:num(g.startMm&&g.startMm.z,0)},outerDiameterMm:g.outerDiameterMm}},windows),
        need=num(r.reserveZone&&r.reserveZone.combustibleClearanceMm,100);
    if(casing&&casing.nearestSurfaceClearanceMm>=need-0.1){
      r.selection.casingClearanceMm=casing.nearestSurfaceClearanceMm;r.selection.casingRequiredMm=need;
      w.forEach(function(x){warnings.push(x)});
      issue(warnings,'FLUE_TWIN_WALL_FROM_OUTLET_UNVERIFIED','flue.selection','The insulated chimney starts '+FLUE_TWIN_WALL_STUB_MM+' mm above the stove outlet because no single-wall arrangement keeps its clearances. Confirm this start height with Harvia before release.');
      return r;
    }
  }
  /* None keeps its clearances: return the preferred route so the clearance check reports the real problem. */
  r1.selection={tier:0,id:'none-compliant',label:'No compliant flue arrangement',detail:'Single-wall, shielded single-wall and insulated chimney all come too close to combustible surfaces at this stove position.'};
  w1.forEach(function(x){warnings.push(x)});
  return r1;
}
function routeVerticalRoof(core,building,system,intent,input,errors,warnings,opts){
  opts=opts||{};
  var outlet=chooseFlueOutlet(core,'vertical-roof',intent,errors);
  if(!outlet)return null;

  var p=outlet.centreMm,room=core.analysis.room,roofHit=roofIntersectionVertical(building,p.x,p.z);
  if(!roofHit){
    issue(errors,'ROOF_INTERSECTION_NOT_FOUND','flue.route','Could not intersect the stove top outlet centreline with the emitted roof-finish geometry.');
    return null;
  }

  var req=flueSystemRequirements(system,errors);
  if(!req)return null;

  var minVisible=req.minVisibleInsulatedMm,
      transitionByCeiling=room.heightMm-minVisible,
      shieldTop=selectedProtectionTopY(core),
      suppliedConnectorLength=req.connectorLengthMm,
      transitionByConnector=p.y+suppliedConnectorLength,
      transitionY=Math.min(transitionByCeiling,transitionByConnector);

  if(shieldTop!=null)transitionY=Math.min(transitionY,shieldTop);
  if(opts.transitionYmm!=null)transitionY=Math.min(transitionY,Math.max(p.y+21,Number(opts.transitionYmm)));
  if(transitionY<=p.y+20){
    issue(errors,'FLUE_TRANSITION_TOO_LOW','flue.transition','Resolved insulated-chimney transition is at or below the usable stove outlet connection height.',
      {outletYmm:p.y,transitionYmm:transitionY});
    return null;
  }

  var outer=req.outerDiameterMm,
      clr=req.casingClearanceMm,
      reserveRadius=outer/2+clr,
      terminalPlan=manufacturerTerminalPlan(building,roofHit,outer,system,req),
      targetTerminalY=terminalPlan.requiredTerminalYmm,
      requiredInsulated=targetTerminalY-transitionY,
      modules;

  if(!(terminalPlan.terminalHeightRule&&terminalPlan.terminalHeightRule.referenceResolved)){
    issue(errors,'TERMINAL_REFERENCE_UNRESOLVED','flue.terminal',
      'The manufacturer terminal-height rule could not resolve the roof ridge/high-edge reference for this roof geometry.',
      {roofFamily:building&&building.metrics&&building.metrics.roof&&building.metrics.roof.family||null});
    return null;
  }

  modules=chooseInsulatedModules(requiredInsulated,req);

  if(!modules){
    issue(errors,'INSULATED_CHIMNEY_LENGTH_UNAVAILABLE','flue.modules','Current WHP1500/WHP500/WHP1000 module set cannot reach the manufacturer terminal-height requirement within the Harvia system maximum chimney height.',
      {requiredLengthMm:requiredInsulated,minimumPhysicalTerminalYmm:targetTerminalY});
    return null;
  }

  var actualTerminalY=transitionY+modules.totalLengthMm,
      singleWallLength=Math.max(0,transitionY-p.y),
      singleWallQty=1,
      conflicts=structuralConflicts(building,p.x,p.z,transitionY,roofHit.pointMm.y,reserveRadius),
      maxSelfSupport=req.maxSelfSupportingHeightMm,
      actualSelfSupport=Math.max(0,actualTerminalY-terminalPlan.highestCasingRoofContactYmm);

  if(actualTerminalY<targetTerminalY-0.1)
    issue(errors,'TERMINAL_TARGET_NOT_REACHED','flue.terminal','Selected chimney modules do not reach the manufacturer terminal-height requirement.',
      {requiredTerminalYmm:targetTerminalY,actualTerminalYmm:actualTerminalY});

  if(actualSelfSupport>maxSelfSupport+0.1)
    issue(errors,'CHIMNEY_SELF_SUPPORT_LIMIT_EXCEEDED','flue.terminal',
      'The compliant terminal height would leave more than the Harvia permitted self-supporting chimney height above the roof. Additional manufacturer-approved support is required before this route can be released.',
      {actualSelfSupportingHeightMm:actualSelfSupport,maximumSelfSupportingHeightMm:maxSelfSupport});

  terminalPlan.actualTerminalYmm=actualTerminalY;
  terminalPlan.actualRiseAboveRoofCentrelineMm=actualTerminalY-roofHit.pointMm.y;
  terminalPlan.actualRiseAboveHighestCasingRoofContactMm=actualTerminalY-terminalPlan.highestCasingRoofContactYmm;
  terminalPlan.actualSelfSupportingHeightMm=actualSelfSupport;
  terminalPlan.maximumSelfSupportingHeightMm=maxSelfSupport;
  terminalPlan.physicalEmergenceValid=actualTerminalY>=terminalPlan.minimumPhysicalTerminalYmm-0.1;
  terminalPlan.terminalHeightValid=actualTerminalY>=targetTerminalY-0.1;
  terminalPlan.manufacturerConstraintsValid=actualSelfSupport<=maxSelfSupport+0.1;
  terminalPlan.manufacturerStatus=terminalPlan.manufacturerConstraintsValid&&terminalPlan.physicalEmergenceValid?
    'manufacturer-compliant':'manufacturer-invalid';
  terminalPlan.terminalHeightStatus=terminalPlan.terminalHeightValid?
    'manufacturer-compliant':'manufacturer-invalid';
  terminalPlan.status=terminalPlan.terminalHeightValid&&terminalPlan.manufacturerConstraintsValid&&terminalPlan.physicalEmergenceValid?
    'manufacturer-compliant':'manufacturer-invalid';

  var segments=[
    {
      id:'heater.01.flue.singlewall.01',
      type:'single-wall',
      componentId:'harvia-wz115100',
      geometry:{type:'cylinder',startMm:clone(p),endMm:{x:p.x,y:transitionY,z:p.z},nominalDiameterMm:115,outerDiameterMm:115}
    },
    {
      id:'heater.01.flue.insulated.01',
      type:'insulated-twin-wall',
      componentId:'harvia-whp1500-module',
      geometry:{type:'cylinder',startMm:{x:p.x,y:transitionY,z:p.z},endMm:{x:p.x,y:actualTerminalY,z:p.z},nominalDiameterMm:115,outerDiameterMm:outer}
    }
  ];

  var penetrations=[
    {
      id:'heater.01.flue.penetration.ceiling',
      hostType:'ceiling',
      hostRef:'interior-ceiling',
      centreMm:{x:p.x,y:room.heightMm,z:p.z},
      axis:{x:0,y:1,z:0},
      flueOuterDiameterMm:outer,
      requiredClearOpeningMm:null,
      reservedClearDiameterMm:2*reserveRadius,
      componentIds:['harvia-whp-ceiling-flange','harvia-whp-leadthrough-insulation','harvia-whp-leadthrough-protector'],
      installationMethod:'site-cut'
    },
    {
      id:'heater.01.flue.penetration.roof',
      hostType:'roof',
      hostRef:roofHit.surfaceId,
      centreMm:clone(roofHit.pointMm),
      axis:{x:0,y:1,z:0},
      flueOuterDiameterMm:outer,
      requiredClearOpeningMm:null,
      reservedClearDiameterMm:2*reserveRadius,
      componentIds:['harvia-whp-rain-flange'],
      installationMethod:'site-cut'
    }
  ];

  return{
    valid:true,
    routeType:'vertical-roof',
    outlet:outlet,
    transition:{
      pointMm:{x:p.x,y:transitionY,z:p.z},
      reason:shieldTop!=null?'lowest of Harvia lead-through visibility, supplied connector length and protective-sheath top':'lower of Harvia lead-through visibility and supplied connector length',
      shieldTopYmm:shieldTop,
      ceilingRuleYmm:transitionByCeiling,
      connectorLimitYmm:transitionByConnector,
      suppliedConnectorLengthMm:suppliedConnectorLength
    },
    roofIntersection:roofHit,
    segments:segments,
    penetrations:penetrations,
    terminal:{
      componentId:'harvia-whp-rain-cap',
      pointMm:{x:p.x,y:actualTerminalY,z:p.z},
      requiredTerminalYmm:targetTerminalY,
      actualRiseAboveRoofCentrelineMm:actualTerminalY-roofHit.pointMm.y,
      actualRiseAboveHighestCasingRoofContactMm:actualTerminalY-terminalPlan.highestCasingRoofContactYmm,
      selfSupportingHeightMm:actualSelfSupport,
      maximumSelfSupportingHeightMm:maxSelfSupport,
      validationStatus:terminalPlan.status,
      terminalHeightAuthority:terminalPlan.terminalHeightAuthority,
      manufacturerStatus:terminalPlan.manufacturerStatus,
      manufacturerValidation:{
        authority:'manufacturer',
        manufacturer:terminalPlan.manufacturer,
        systemId:terminalPlan.systemId,
        systemModel:terminalPlan.systemModel,
        physicalEmergenceValid:terminalPlan.physicalEmergenceValid,
        manufacturerConstraintsValid:terminalPlan.manufacturerConstraintsValid,
        actualSelfSupportingHeightMm:terminalPlan.actualSelfSupportingHeightMm,
        maximumSelfSupportingHeightMm:terminalPlan.maximumSelfSupportingHeightMm,
        manufacturerConstraints:clone(terminalPlan.manufacturerConstraints),
        status:terminalPlan.manufacturerStatus
      },
      complianceStatus:terminalPlan.status,
      compliance:clone(terminalPlan)
    },
    modulePlan:modules,
    singleWallPlan:{
      requiredLengthMm:singleWallLength,
      cutLengthMm:singleWallLength,
      suppliedLengthMm:suppliedConnectorLength,
      componentId:'harvia-wz115100',
      quantity:singleWallQty,
      installation:'cut supplied 1.0 m WZ115100 to resolved length',
      procurementStatus:'resolved-from-WHP1500-kit',
      protectionRequest:opts.protectionRequest||null
    },
    selection:opts.selection||null,
    reserveZone:{
      id:'heater.01.flue.reserve.vertical',
      type:'penetration-reservation',
      centreline:{x:p.x,z:p.z,y0Mm:transitionY,y1Mm:roofHit.pointMm.y},
      radiusMm:reserveRadius,
      outerDiameterMm:outer,
      combustibleClearanceMm:clr
    },
    affectedStructuralMemberIds:conflicts
  };
}

function routeRearWall(core,building,system,intent,errors,warnings){
  var outlet=chooseFlueOutlet(core,'rear-wall-vertical',intent,errors);
  if(!outlet)return null;

  issue(errors,'FLUE_SYSTEM_ROUTE_UNSUPPORTED','flue.route',
    'The current Harvia WHP1500 catalogue record is vertical-only. A compliant insulated wall-pass-through / external tee system must be catalogued before rear-wall-vertical routing can be released.');
  return{
    valid:false,
    routeType:'rear-wall-vertical',
    outlet:outlet,
    status:'reserved-not-released'
  };
}

function resolveRoute(core,building,input,errors,warnings){
  var c=catalogue(),intent=core&&core.analysis&&core.analysis.project?normaliseHeaterIntent(core.analysis.project,input):null,
      systemId=intent&&intent.flue&&intent.flue.flueSystemId||'harvia-whp1500',
      system=c&&c.getFlueSystem&&c.getFlueSystem(systemId);

  if(!system){
    issue(errors,'FLUE_SYSTEM_UNKNOWN','flue.flueSystemId','Selected flue system is not present in Heater Catalogue.');
    return null;
  }

  var pref=intent.flue.routePreference||'auto',
      routeType=pref==='auto'?'vertical-roof':pref;

  if(routeType==='vertical-roof')return{systemId:systemId,system:system,route:routeVerticalRoofAuto(core,building,system,intent,input,errors,warnings),intent:intent};
  if(routeType==='rear-wall-vertical')return{systemId:systemId,system:system,route:routeRearWall(core,building,system,intent,errors,warnings),intent:intent};

  issue(errors,'FLUE_ROUTE_UNKNOWN','flue.routePreference','Unsupported flue route preference.');
  return null;
}

function manifestItem(instanceId,catalogueComponentId,category,quantity,order,parent,geometryRef,suppliedAs,note){
  return{
    instanceId:instanceId,
    catalogueComponentId:catalogueComponentId,
    category:category,
    quantity:quantity,
    suppliedAsPartOf:suppliedAs||null,
    installOrder:order,
    parentInstanceId:parent||null,
    geometryRef:geometryRef||null,
    procurement:{includeInMaterials:true,note:note||null}
  };
}


function resolvedSingleWallPipeClearance(core,route){
  var room=core&&core.analysis&&core.analysis.room||null,
      seg=finalList(route&&route.segments).find(function(x){return String(x&&x.type||'')==='single-wall'})||null;
  if(!seg)return{segment:null,clearance:null,lengthMm:null};
  var g=seg.geometry||{},a=g.startMm||{},b=g.endMm||{};
  return{
    segment:seg,
    clearance:roomWallClearanceFromVerticalPipe(room,seg,clearanceSurfaces(core)),
    lengthMm:Math.abs(num(b.y,0)-num(a.y,0))
  };
}
function resolveSingleWallProtection(core,route,intent,input){
  var c=catalogue(),q=resolvedSingleWallPipeClearance(core,route),
      seg=q.segment,pc=q.clearance,L=q.lengthMm,
      pipe=c&&c.getInstallationComponent&&c.getInstallationComponent('harvia-wz115100'),
      required=num(pipe&&pipe.safety&&pipe.safety.combustibleRadialClearanceMm,500),
      request=clone(intent&&intent.singleWallProtection||input&&input.singleWallProtection||route&&route.singleWallPlan&&route.singleWallPlan.protectionRequest||{mode:'auto',componentId:null}),
      actual=pc&&pc.nearestSurfaceClearanceMm;

  if(!seg)return{active:false,status:'not-applicable',mode:'none',componentId:null,placements:[]};
  if(actual!=null&&actual>=required-0.1)return{
    active:false,status:'not-required',mode:'none',componentId:null,placements:[],
    actualMinimumMm:actual,unprotectedRequiredMm:required,nearestWall:pc.nearestWall,singleWallLengthMm:L
  };

  var componentId=String(request&&request.componentId||''),
      mode=String(request&&request.mode||'auto'),
      component=componentId&&c&&c.getInstallationComponent?c.getInstallationComponent(componentId):null;

  /* Harvia WZ020130 remains a single documented guard. Multiple WZ020130 guards
     are deliberately NOT inferred or allowed without Harvia documentation. */
  if(componentId==='harvia-wz020130'){
    /* The shield only has to cover the stretch that is actually too close (e.g. below a window sill), not the whole run. */
    var st=failStretches(pc,required),segY0=Math.min(num(seg.geometry.startMm&&seg.geometry.startMm.y,0),num(seg.geometry.endMm&&seg.geometry.endMm.y,0)),
        stretchStart=st.length?st[0].y0:segY0,stretchEnd=st.length?Math.min(st[st.length-1].y1+10,segY0+L):segY0+L;
    if(st.length)L=Math.max(0,stretchEnd-stretchStart);
    var gh=num(component&&component.physical&&component.physical.heightMm,235),
        protectedClear=num(component&&component.safetyEffect&&component.safetyEffect.protectedPipeClearanceMm,250);
    if(L<=gh+0.1&&actual!=null&&actual>=protectedClear-0.1){
      return{
        active:true,status:'resolved-single-harvia-guard',mode:'manufacturer-guard',
        componentId:componentId,quantity:1,protectedClearanceMm:protectedClear,
        actualMinimumMm:actual,unprotectedRequiredMm:required,nearestWall:pc.nearestWall,
        singleWallLengthMm:L,coverageLengthMm:gh,documentationConfirmed:true,
        placements:[{id:'heater.01.flue.pipe-protection.01',componentId:componentId,startYmm:stretchStart,endYmm:stretchStart+L}]
      };
    }
    return{
      active:false,status:L>gh+0.1?'harvia-guard-too-short-single-only':'harvia-guard-clearance-insufficient',
      mode:'manufacturer-guard',componentId:componentId,quantity:0,placements:[],
      actualMinimumMm:actual,unprotectedRequiredMm:required,protectedClearanceMm:protectedClear,
      nearestWall:pc&&pc.nearestWall||null,singleWallLengthMm:L,singleGuardHeightMm:gh,
      multipleHarviaGuardsAllowed:false,
      note:'Multiple WZ020130 guards are not authorised unless Harvia documentation confirms the serial arrangement.'
    };
  }

  /* Documented third-party / equivalent protector.
     It is only accepted when the caller/project explicitly confirms:
       - actual-pipe compatibility,
       - full-length coverage,
       - protected clearance,
       - documentation authority.
     This allows Jeremias or equivalent products without silently transferring
     certification from one manufacturer's pipe system to another. */
  if(mode==='documented-third-party'||mode==='documented-external'||componentId){
    var candidate=component||{},
        compatConfirmed=request.compatibilityConfirmed===true||request.compatibleWithPipeComponentId==='harvia-wz115100',
        docsConfirmed=request.documentationConfirmed===true,
        protectedMm=num(request.protectedClearanceMm,null),
        lengthMm=num(request.coverageLengthMm,num(candidate&&candidate.physical&&candidate.physical.nominalLengthMm,null)),
        manufacturer=String(request.manufacturer||candidate&&candidate.identity&&candidate.identity.manufacturer||''),
        model=String(request.model||candidate&&candidate.identity&&candidate.identity.model||componentId||'documented protector');

    if(componentId==='jeremias-ferro1961-dn115-candidate'&&!compatConfirmed){
      return{
        active:false,status:'candidate-compatibility-unconfirmed',mode:'documented-third-party',
        componentId:componentId,manufacturer:manufacturer||'Jeremias',model:model||'FERRO1961',
        quantity:0,placements:[],actualMinimumMm:actual,unprotectedRequiredMm:required,
        singleWallLengthMm:L,nearestWall:pc&&pc.nearestWall||null,
        candidateEvidence:clone(candidate),
        note:'Jeremias documents the DN115 FERRO pipe protector, but compatibility with Harvia WZ115100 must be confirmed before this installation can rely on it.'
      };
    }

    if(docsConfirmed&&compatConfirmed&&protectedMm!=null&&lengthMm!=null&&lengthMm>=L-0.1&&actual!=null&&actual>=protectedMm-0.1){
      var y0=Math.min(num(seg.geometry.startMm&&seg.geometry.startMm.y,0),num(seg.geometry.endMm&&seg.geometry.endMm.y,0)),
          y1=y0+L;
      return{
        active:true,status:'resolved-documented-third-party',mode:'documented-third-party',
        componentId:componentId||'external-documented-pipe-protector',
        manufacturer:manufacturer,model:model,quantity:1,
        protectedClearanceMm:protectedMm,actualMinimumMm:actual,
        unprotectedRequiredMm:required,nearestWall:pc.nearestWall,
        singleWallLengthMm:L,coverageLengthMm:lengthMm,
        documentationConfirmed:true,compatibilityConfirmed:true,
        documentationReference:request.documentationReference||null,
        placements:[{
          id:'heater.01.flue.pipe-protection.01',
          componentId:componentId||'external-documented-pipe-protector',
          startYmm:y0,endYmm:y1,heightMm:L,
          displayType:'pipe-mounted-radiation-shield'
        }]
      };
    }

    return{
      active:false,status:'documented-protector-incomplete',mode:'documented-third-party',
      componentId:componentId||null,manufacturer:manufacturer,model:model,
      quantity:0,placements:[],actualMinimumMm:actual,unprotectedRequiredMm:required,
      protectedClearanceMm:protectedMm,coverageLengthMm:lengthMm,singleWallLengthMm:L,
      documentationConfirmed:docsConfirmed,compatibilityConfirmed:compatConfirmed,
      requiredConfirmations:[
        'documentationConfirmed',
        'compatibilityConfirmed for Harvia WZ115100 (or exact selected pipe)',
        'protectedClearanceMm',
        'coverageLengthMm >= complete single-wall length'
      ]
    };
  }

  return{
    active:false,status:'protection-required',mode:'auto',componentId:null,quantity:0,placements:[],
    actualMinimumMm:actual,unprotectedRequiredMm:required,nearestWall:pc&&pc.nearestWall||null,
    singleWallLengthMm:L,
    allowedAlternatives:[
      {
        type:'manufacturer-guard',
        componentId:'harvia-wz020130',
        rule:'single documented guard only; multiple guards require Harvia documentation'
      },
      {
        type:'documented-third-party',
        candidateComponentId:'jeremias-ferro1961-dn115-candidate',
        rule:'allowed only after documentation confirms compatibility with the actual pipe, full coverage and resulting clearance'
      },
      {
        type:'documented-third-party',
        candidateComponentId:null,
        rule:'equivalent certified/documented pipe protector may be used when the same confirmations are supplied'
      }
    ]
  };
}

function buildComponentManifest(core,routePack){
  var out=[],order=10,stove=core.assembly.stove,protection=core.assembly.protection,floor=core.assembly.floor,route=routePack.route,systemId=routePack.systemId;

  out.push(manifestItem('heater.01.stove',stove.modelId,'stove',1,order+=10,null,'heater.01.body',null,null));

  (protection.items||[]).forEach(function(x,i){
    out.push(manifestItem(x.instanceId,x.systemId,'stove-protection',1,order+=10,'heater.01.stove',null,null,'Manufacturer-approved heater protection.'));
  });

  if(floor&&floor.systemId)
    out.push(manifestItem('heater.01.floor',floor.systemId,'floor-protection',1,order+=10,'heater.01.stove',null,null,'Floor protection selection is provisional until floor construction classification is integrated.'));

  out.push(manifestItem('heater.01.flue.singlewall','harvia-wz115100','single-wall-flue',route.singleWallPlan.quantity,order+=10,'heater.01.stove','heater.01.flue.singlewall.01',systemId,'Use the WHP1500 supplied 1.0 m WZ115100 smoke pipe and cut it to the resolved length.'));

  var swp=route.singleWallPlan&&route.singleWallPlan.protection;
  if(swp&&swp.active&&swp.componentId){
    out.push(manifestItem(
      'heater.01.flue.pipe-protection',
      swp.componentId,
      'flue-protection',
      swp.quantity||1,
      order+=10,
      'heater.01.flue.singlewall',
      'heater.01.flue.singlewall.01',
      null,
      swp.mode==='documented-third-party'?
        'Documented third-party pipe protector; compatibility and clearance authority are recorded in the resolved protection plan.':
        'Manufacturer smoke-pipe radiation protection.'
    ));
  }

  route.modulePlan.modules.forEach(function(m,i){
    out.push(manifestItem('heater.01.chimney.module.'+(i+1),m.componentId,'insulated-chimney',m.quantity,order+=10,'heater.01.flue.singlewall','heater.01.flue.insulated.01',systemId,null));
  });

  out.push(manifestItem('heater.01.penetration.ceiling.flange','harvia-whp-ceiling-flange','penetration',1,order+=10,'heater.01.chimney.module.1','heater.01.flue.penetration.ceiling',systemId,null));
  out.push(manifestItem('heater.01.penetration.insulation','harvia-whp-leadthrough-insulation','penetration',1,order+=10,'heater.01.chimney.module.1','heater.01.flue.penetration.ceiling',systemId,null));
  out.push(manifestItem('heater.01.penetration.protector','harvia-whp-leadthrough-protector','penetration',1,order+=10,'heater.01.chimney.module.1','heater.01.flue.penetration.ceiling',systemId,null));
  out.push(manifestItem('heater.01.roof.flashing','harvia-whp-rain-flange','weathering',1,order+=10,'heater.01.chimney.module.1','heater.01.flue.penetration.roof',systemId,null));
  out.push(manifestItem('heater.01.terminal','harvia-whp-rain-cap','terminal',1,order+=10,'heater.01.chimney.module.1',null,systemId,null));

  return out;
}

function buildInstallationSequence(core,routePack,manifest){
  var route=routePack.route;
  function ids(cat){return manifest.filter(function(x){return x.category===cat}).map(function(x){return x.instanceId})}
  return[
    {id:'heater.install.01.floor',order:10,title:'Install floor protection',componentInstanceIds:ids('floor-protection'),geometryRefs:[],buildingRequirementIds:['heater.01.requirement.floor-load-check'],notes:[]},
    {id:'heater.install.02.stove',order:20,title:'Position stove',componentInstanceIds:ids('stove'),geometryRefs:['heater.01.body'],buildingRequirementIds:[],notes:['Set the stove to the resolved manufacturer clearance geometry.']},
    {id:'heater.install.03.protection',order:30,title:'Install stove protection',componentInstanceIds:ids('stove-protection'),geometryRefs:[],buildingRequirementIds:[],notes:[]},
    {id:'heater.install.04.singlewall',order:40,title:'Install single-wall connector',componentInstanceIds:ids('single-wall-flue').concat(ids('flue-protection')),geometryRefs:['heater.01.flue.singlewall.01'],buildingRequirementIds:[],notes:[
      route.singleWallPlan&&route.singleWallPlan.protection&&route.singleWallPlan.protection.active?
        'Install the resolved documented smoke-pipe protection over the complete protected section.':
        'No smoke-pipe protector has been accepted by the resolved installation.'
    ]},
    {id:'heater.install.05.transition',order:50,title:'Transition to insulated chimney',componentInstanceIds:ids('insulated-chimney'),geometryRefs:['heater.01.flue.insulated.01'],buildingRequirementIds:[],notes:['Transition height follows the lower/more restrictive Harvia sheath/lead-through requirement.']},
    {id:'heater.install.06.penetration',order:60,title:'Prepare ceiling and roof penetrations',componentInstanceIds:ids('penetration'),geometryRefs:['heater.01.flue.penetration.ceiling','heater.01.flue.penetration.roof'],buildingRequirementIds:['heater.01.requirement.flue-clear-zone','heater.01.requirement.roof-opening','heater.01.requirement.penetration-fire-protection'],notes:['Building Geometry must adapt around the reserved flue clear zone.']},
    {id:'heater.install.07.chimney',order:70,title:'Install insulated chimney',componentInstanceIds:ids('insulated-chimney'),geometryRefs:['heater.01.flue.insulated.01'],buildingRequirementIds:[],notes:[]},
    {id:'heater.install.08.weathering',order:80,title:'Weather roof penetration',componentInstanceIds:ids('weathering'),geometryRefs:['heater.01.flue.penetration.roof'],buildingRequirementIds:['heater.01.requirement.weathering'],notes:[]},
    {id:'heater.install.09.terminal',order:90,title:'Install terminal',componentInstanceIds:ids('terminal'),geometryRefs:[],buildingRequirementIds:[],notes:['Terminal assembly and chimney height follow the selected chimney manufacturer system limits. Regional terminal regulations are not evaluated in this version.']},
    {id:'heater.install.10.check',order:100,title:'Final clearance and access check',componentInstanceIds:[],geometryRefs:core.safety.clearanceZones.concat(core.safety.operationalZones||[]).map(function(x){return x.id}),buildingRequirementIds:['heater.01.requirement.firebox-access'],notes:['Verify heater/chimney clearances and keep the firebox loading/ash-removal access zone unobstructed before commissioning.']}
  ];
}

function buildingRequirementsForRoute(core,routePack){
  var route=routePack.route,req=clone(core.buildingRequirements||[]);
  req.push({
    id:'heater.01.requirement.flue-clear-zone',
    type:'reserve-clear-zone',
    status:'required',
    priority:'heater-system',
    geometryRef:route.reserveZone.id,
    hostRef:null,
    affectedMemberIds:clone(route.affectedStructuralMemberIds||[]),
    note:'Reserve the insulated chimney clear zone. Building framing must adapt around this geometry.'
  });
  if((route.affectedStructuralMemberIds||[]).length){
    req.push({
      id:'heater.01.requirement.structural-adjustment',
      type:'structural-adjustment-required',
      status:'required',
      priority:'heater-system',
      geometryRef:route.reserveZone.id,
      hostRef:'roof/ceiling structure',
      affectedMemberIds:clone(route.affectedStructuralMemberIds),
      note:'Do not move the stove/flue to avoid these members; Building Geometry must later frame around the reserved chimney opening.'
    });
  }
  req.push({
    id:'heater.01.requirement.roof-opening',
    type:'roof-opening-required',
    status:'required',
    priority:'heater-system',
    geometryRef:'heater.01.flue.penetration.roof',
    hostRef:route.roofIntersection.surfaceId,
    affectedMemberIds:[],
    note:'Site-cut roof penetration; detailed trimming rules are deferred to Building Geometry.'
  });
  req.push({
    id:'heater.01.requirement.penetration-fire-protection',
    type:'penetration-fire-protection-required',
    status:'required',
    priority:'heater-system',
    geometryRef:'heater.01.flue.penetration.ceiling',
    hostRef:'ceiling/roof build-up',
    affectedMemberIds:[],
    note:'Use the approved Harvia lead-through insulation/protector system.'
  });
  req.push({
    id:'heater.01.requirement.weathering',
    type:'weathering-detail-required',
    status:'required',
    priority:'heater-system',
    geometryRef:'heater.01.flue.penetration.roof',
    hostRef:route.roofIntersection.surfaceId,
    affectedMemberIds:[],
    note:'Provide the approved roof weathering/flashing arrangement.'
  });
  return req;
}

function resolveInstallation(input){
  input=input||{};
  var errors=[],warnings=[],core=resolveCore(input);
  (core.errors||[]).forEach(function(x){errors.push(x)});
  (core.warnings||[]).forEach(function(x){warnings.push(x)});
  if(!core.valid)return{valid:false,engineVersion:VERSION,stage:STAGE,core:core,errors:errors,warnings:warnings};

  var p=core.analysis.project,b=generateBuilding(p);
  if(!b.available){
    issue(errors,'BUILDING_ENGINE_UNAVAILABLE','environment.building','Building Geometry Engine is required for Stage 2F heater/flue resolution.');
    return{valid:false,engineVersion:VERSION,stage:STAGE,core:core,errors:errors,warnings:warnings};
  }
  if(!b.valid){
    issue(errors,'BUILDING_GEOMETRY_INVALID','building','Building Geometry could not be generated.',b.errors);
    return{valid:false,engineVersion:VERSION,stage:STAGE,core:core,building:b,errors:errors,warnings:warnings};
  }

  (b.warnings||[]).forEach(function(x){issue(warnings,'BUILDING_WARNING','building',String(x))});

  var rp=resolveRoute(core,b.result,input,errors,warnings);
  if(!rp||!rp.route||!rp.route.valid)
    return{valid:false,engineVersion:VERSION,stage:STAGE,core:core,building:{designId:b.result&&b.result.designId,metrics:b.result&&b.result.metrics},route:rp,errors:errors,warnings:warnings};

  rp.route.singleWallPlan=rp.route.singleWallPlan||{};
  rp.route.singleWallPlan.protection=resolveSingleWallProtection(core,rp.route,rp.intent,input);

  var manifest=buildComponentManifest(core,rp),
      requirements=buildingRequirementsForRoute(core,rp),
      sequence=buildInstallationSequence(core,rp,manifest),
      result={
        schemaVersion:1,
        heaterId:core.assembly.stove.id,
        catalogueVersion:catalogue().version,
        engineVersion:VERSION,
        coordinateSystemId:COORD,
        units:UNITS,

        selection:clone(core.selection),

        assembly:{
          stove:clone(core.assembly.stove),
          protection:clone(core.assembly.protection),
          floor:clone(core.assembly.floor),
          flue:{
            systemId:rp.systemId,
            routeType:rp.route.routeType,
            outlet:clone(rp.route.outlet),
            transition:clone(rp.route.transition),
            segments:clone(rp.route.segments),
            penetrations:clone(rp.route.penetrations),
            terminal:clone(rp.route.terminal),
            modulePlan:clone(rp.route.modulePlan),
            singleWallPlan:clone(rp.route.singleWallPlan),
            selection:clone(rp.route.selection||null),
            reserveZone:clone(rp.route.reserveZone)
          }
        },

        safety:{
          clearanceZones:clone(core.safety.clearanceZones),
          operationalZones:clone(core.safety.operationalZones||[]),
          flueClearanceZones:[clone(rp.route.reserveZone)]
        },

        buildingRequirements:requirements,
        componentManifest:manifest,
        installationSequence:sequence,

        /*
         * The Installation Contract requires generated output to carry a
         * validation object. Seed it before contract validation, then replace
         * it with the actual contract result below.
         */
        validation:{
          valid:null,
          contractVersion:contract()&&contract().version||null,
          errors:[],
          warnings:[],
          status:'pending-contract-validation'
        }
      };

  var ct=contract(),cv=ct&&typeof ct.validateResolvedInstallation==='function'?ct.validateResolvedInstallation(result):{valid:true,errors:[],warnings:[]};

  if(!cv.valid)(cv.errors||[]).forEach(function(x){issue(errors,'INSTALLATION_CONTRACT_INVALID','resolvedInstallation',x.message||String(x),x)});

  var ra=roofAccommodation(),accommodation=null;
  if(ra&&typeof ra.apply==='function'){
    try{
      accommodation=ra.apply(b.result,result);
      if(accommodation&&accommodation.errors)(accommodation.errors||[]).forEach(function(x){issue(errors,'ROOF_ACCOMMODATION_INVALID','building.roofAccommodation',x.message||String(x),x)});
      if(accommodation&&accommodation.warnings)(accommodation.warnings||[]).forEach(function(x){issue(warnings,'ROOF_ACCOMMODATION_WARNING','building.roofAccommodation',x.message||String(x),x)});
    }catch(e){
      issue(errors,'ROOF_ACCOMMODATION_EXCEPTION','building.roofAccommodation','Roof accommodation failed: '+String(e&&e.message||e));
    }
  }else{
    issue(errors,'ROOF_ACCOMMODATION_ENGINE_MISSING','environment.roofAccommodation','ThermoAspenHeaterRoofAccommodationV1 is required for Stage 2F.');
  }

  var finalAssessment=finalInstallationAssessment(result,{
    core:core,
    project:p,
    input:input,
    contractValidation:cv,
    roofAccommodation:accommodation,
    building:b.result
  });

  result.validation={
    valid:cv.valid&&finalAssessment.overallStatus!=='fail',
    contractVersion:ct&&ct.version||null,
    errors:clone(cv.errors||[]),
    warnings:clone(cv.warnings||[]),
    status:'final-assessment-complete',
    terminalManufacturerValidation:clone(rp.route.terminal&&(rp.route.terminal.manufacturerValidation||rp.route.terminal.compliance)||null),
    terminalCompliance:clone(rp.route.terminal&&rp.route.terminal.compliance||null),
    finalAssessment:clone(finalAssessment),
    designLimitations:[
      'Rear-wall-vertical flue routing remains reserved until an approved horizontal wall-pass-through/external chimney system is catalogued.',
      'A conditional final assessment is not a failure: it means one or more explicit project/site assumptions still need confirmation before release.',
      'Final validation does not hide resolved geometry when a safety check fails; the installation remains visible so the conflicting condition can be corrected.'
    ]
  };

  return{
    valid:errors.length===0&&cv.valid,
    contractComplete:cv.valid,
    designComplete:finalAssessment.overallStatus==='pass',
    releaseReady:finalAssessment.releaseReady,
    finalValidation:clone(finalAssessment),
    engineVersion:VERSION,
    stage:STAGE,
    result:result,
    core:core,
    building:{
      designId:b.result.designId,
      engineVersion:b.result.engineVersion,
      roof:clone(b.result.metrics&&b.result.metrics.roof||null)
    },
    roofAccommodation:accommodation?{
      valid:!!accommodation.valid,
      version:accommodation.version||null,
      plan:clone(accommodation.plan||null),
      adaptedBuilding:clone(accommodation.building||null)
    }:null,
    errors:errors,
    warnings:warnings
  };
}



/* --------------------------------------------------------------------------
   Stage 2F — final installation validation.

   This pass is deliberately diagnostic. It does not move the stove, reroute the
   flue or hide otherwise renderable geometry when a safety/layout check fails.
   Consumers can therefore keep showing the resolved installation while the
   finalAssessment explains what is passed, conditional or blocking.
   -------------------------------------------------------------------------- */

function finalCheck(id,label,status,message,data){
  return{
    id:String(id||''),
    label:String(label||id||''),
    status:String(status||'conditional'),
    message:String(message||''),
    data:data===undefined?null:clone(data)
  };
}
function finalStatusRank(s){return s==='fail'?3:s==='conditional'?2:s==='pass'?1:0}
function finalList(v){return Array.isArray(v)?v:[]}
function addFinalCheck(list,id,label,status,message,data){
  list.push(finalCheck(id,label,status,message,data));
}
/* Glass is non-combustible; the window FRAME (and the wall around it) is combustible. A pipe that sits wholly in front of the glass is
 * therefore measured to the nearest frame edge, not to the wall plane. The frame border width is an assumption until a window
 * schedule supplies it (opening.frameBorderMm overrides). */
var WINDOW_FRAME_BORDER_MM=60;
function windowsForClearance(project){
  return openingList(project).filter(function(o){return o&&o.enabled!==false&&openingType(o)==='window'&&['front','back','left','right'].indexOf(o.wall)>=0}).map(function(o){
    var w=num(o.frameWidthMm,num(o.widthMm,0)),h=num(o.frameHeightMm,num(o.heightMm,0)),b=num(o.frameBorderMm,WINDOW_FRAME_BORDER_MM);
    return{wall:o.wall,a0:num(o.offsetMm,0)+b,a1:num(o.offsetMm,0)+w-b,v0:num(o.sillHeightMm,0)+b,v1:num(o.sillHeightMm,0)+h-b};
  }).filter(function(g){return g.a1>g.a0&&g.v1>g.v0});
}
/* The stove-attached shield (e.g. WL475) protects the cladding immediately behind it, up to the shield's own height.
 * Those wall patches count as protected (like glass) when the flue pipe is measured to combustible surfaces. */
function shieldPatchesForClearance(core){
  var asm=core&&core.assembly,st=asm&&asm.stove,geom=st&&st.geometry,items=asm&&asm.protection&&asm.protection.items||[],room=core&&core.analysis&&core.analysis.room,
      body=geom&&geom.body&&geom.body.aabbMm,top=null,margin=0,hasRear=false,hasSide=false,out=[];
  if(!body||!items.length||!room||!st.wall)return out;
  var cage=null;
  items.forEach(function(it){
    var sys=it&&it.system||{},ph=sys.physical||{},h=num(ph.heightMm,null),pos=String(it.position||'');
    if(pos==='full'&&h!=null){
      var w0=num(geom.body.widthMm,0),d0=num(geom.body.depthMm,0),ow=Math.max(w0,num(ph.widthMm,w0)),od=Math.max(d0,num(ph.depthMm,d0)),
          t=Math.max(18,Math.min(32,(ow-w0)/2||30)),gap=num(st.wallGapMm,0),sz=(d0-od)/2;
      cage={d0:gap+sz,t:t,d1:gap+sz+od,top:h};
    }
    if(h!=null)top=top==null?h:Math.min(top,h);
    if(pos==='rear'||pos==='full')hasRear=true;
    if(pos==='left'||pos==='right'||pos==='full')hasSide=true;
    if(pos==='full'&&num(ph.widthMm,0)>num(geom.body.widthMm,0))margin=Math.max(margin,(num(ph.widthMm,0)-num(geom.body.widthMm,0))/2);
  });
  if(top==null)return out;
  var L=num(room.lengthMm,0),W=num(room.widthMm,0),mn=body.min,mx=body.max;
  var info={left:{d:mn.x,a0:mn.z,a1:mx.z},right:{d:L-mx.x,a0:mn.z,a1:mx.z},back:{d:mn.z,a0:mn.x,a1:mx.x},front:{d:W-mx.z,a0:mn.x,a1:mx.x}};
  var opposite={left:'right',right:'left',back:'front',front:'back'};
  Object.keys(info).forEach(function(wall){
    var q=info[wall];
    if(wall===st.wall){if(hasRear)out.push({wall:wall,a0:q.a0-margin,a1:q.a1+margin,v0:0,v1:top,source:'stove-shield',cage:cage})}
    else if(wall!==opposite[st.wall]&&hasSide&&q.d<=700)out.push({wall:wall,a0:q.a0,a1:q.a1,v0:0,v1:top,source:'stove-shield'});
  });
  return out;
}
function clearanceSurfaces(core,project){
  return windowsForClearance(project||core&&core.analysis&&core.analysis.project).concat(shieldPatchesForClearance(core));
}
/* Height ranges where the pipe is closer than limitMm to a combustible surface. */
function failStretches(cl,limitMm){
  var prof=cl&&cl.profile||[],out=[],cur=null;
  prof.forEach(function(q){
    if(q.c<limitMm-0.1){if(!cur){cur={y0:q.y,y1:q.y,min:q.c};out.push(cur)}cur.y1=q.y;cur.min=Math.min(cur.min,q.c)}
    else cur=null;
  });
  return out;
}
/* In-plane distance from (along,y) on a wall to the nearest COMBUSTIBLE wall point. Glass and shield-protected patches
 * (rects) are non-combustible; 0 means the point itself is combustible cladding. Capped at capMm. */
function inPlaneToCombustible(rects,along,y,spanMm,heightMm,plainMm,centreDistMm){
  var cageRect=null,glass=[];
  rects.forEach(function(g){if(g.cage)cageRect=g;else glass.push(g)});
  if(cageRect){
    /* The shield surrounds the stove's sides and back (and so the pipe up to the shield top). A wall point is protected
     * when the straight line from the pipe to it passes through a shield panel below the shield top. */
    var cg=cageRect.cage,dc=num(centreDistMm,plainMm),best=Infinity,cap=700,step=15,a,v,d,k,ins;
    var occluded=function(sa,yt){
      var u,lat,dep,hgt,L,i,sides=[cageRect.a0,cageRect.a1];
      if(dc>0){ // back panel
        u=(dc-(cg.d0+cg.t/2))/dc;
        if(u>0&&u<1){lat=along+(sa-along)*u;hgt=y+(yt-y)*u;if(lat>=cageRect.a0&&lat<=cageRect.a1&&hgt<=cg.top)return true}
      }
      for(i=0;i<2;i++){ // side panels
        L=sides[i];
        if((sa-L)*(along-L)<0){
          u=(L-along)/(sa-along);dep=dc*(1-u);hgt=y+(yt-y)*u;
          if(dep>=cg.d0&&dep<=cg.d1&&hgt<=cg.top)return true;
        }
      }
      return false;
    };
    for(a=Math.max(0,along-cap);a<=Math.min(spanMm,along+cap);a+=step){
      for(v=Math.max(0,y-cap);v<=Math.min(heightMm,y+cap);v+=step){
        d=Math.hypot(a-along,v-y);if(d>=best)continue;
        ins=false;for(k=0;k<glass.length;k++){var gg=glass[k];if(a>gg.a0&&a<gg.a1&&v>gg.v0&&v<gg.v1){ins=true;break}}
        if(ins)continue;
        if(occluded(a,v))continue;
        best=d;
      }
    }
    return best===Infinity?cap:Math.min(best,cap);
  }
  rects=glass.concat(rects.filter(function(g){return g.cage}));
  function inside(a,v){for(var i=0;i<rects.length;i++){var g=rects[i];if(a>g.a0&&a<g.a1&&v>g.v0&&v<g.v1)return true}return false}
  if(!inside(along,y))return 0;
  var best=Infinity,step=10,cap=Math.max(0,700);
  rects.forEach(function(g){
    var edges=[[g.a0,g.v0,g.a1,g.v0],[g.a0,g.v1,g.a1,g.v1],[g.a0,g.v0,g.a0,g.v1],[g.a1,g.v0,g.a1,g.v1]];
    edges.forEach(function(e){
      var dx=e[2]-e[0],dy=e[3]-e[1],n=Math.max(1,Math.ceil(Math.max(Math.abs(dx),Math.abs(dy))/step)),i,a,v,d;
      for(i=0;i<=n;i++){
        a=e[0]+dx*i/n;v=e[1]+dy*i/n;
        if(a<=0.5||a>=spanMm-0.5||v<=0.5||v>=heightMm-0.5)continue; // room edge: no wall surface beyond
        d=Math.hypot(a-along,v-y);
        if(d<best&&!inside(a+(dx===0?(e[0]===g.a0?-0.7:0.7):0),v+(dy===0?(e[1]===g.v0?-0.7:0.7):0)))best=d;
      }
    });
  });
  return best===Infinity?cap:Math.min(best,cap);
}
function roomWallClearanceFromVerticalPipe(room,segment,windows){
  var g=segment&&segment.geometry||{},p=g.startMm||{},d=num(g.outerDiameterMm,num(g.nominalDiameterMm,115)),
      L=num(room&&room.lengthMm,0),W0=num(room&&room.widthMm,0),r=d/2;
  if(!(L>0&&W0>0&&num(p.x,null)!=null&&num(p.z,null)!=null&&d>0))return null;
  var vals=[
    {wall:'left',centreMm:p.x,surfaceClearanceMm:p.x-r},
    {wall:'right',centreMm:L-p.x,surfaceClearanceMm:L-p.x-r},
    {wall:'back',centreMm:p.z,surfaceClearanceMm:p.z-r},
    {wall:'front',centreMm:W0-p.z,surfaceClearanceMm:W0-p.z-r}
  ];
  /* Clearance is evaluated height by height. Window glass is non-combustible; the frame border and the wall
   * around it are combustible. At each height the pipe is measured to the nearest combustible surface: the
   * wall plane, or (in front of glass) the 3D distance to the frame edge. */
  var ya=num(g.startMm&&g.startMm.y,0),yb=num(g.endMm&&g.endMm.y,0),y0=Math.min(ya,yb),y1=Math.max(ya,yb),
      ys=[],yy;
  for(yy=y0;yy<y1;yy+=10)ys.push(yy);
  ys.push(y1);
  var profile=ys.map(function(y){return{y:y,c:Infinity,wall:null}});
  vals.forEach(function(v){
    var along=(v.wall==='front'||v.wall==='back')?p.x:p.z,plain=v.surfaceClearanceMm,minC=Infinity,usedGlass=false;
    v.plainSurfaceClearanceMm=plain;
    var rects=(windows||[]).filter(function(gl){return gl.wall===v.wall}),span=(v.wall==='front'||v.wall==='back')?L:W0,hh=num(room&&room.heightMm,2100);
    ys.forEach(function(y,k){
      var c=plain;
      if(rects.length){
        var din=inPlaneToCombustible(rects,along,y,span,hh,plain,plain+r);
        if(din>r){var inPlane=din-r,cc=Math.sqrt(plain*plain+inPlane*inPlane);if(cc>c)c=cc}
      }
      if(c<minC){minC=c;usedGlass=c>plain+0.01}
      if(c<profile[k].c){profile[k].c=c;profile[k].wall=v.wall}
    });
    v.surfaceClearanceMm=minC;if(usedGlass)v.measuredToWindowFrame=true;
  });
  vals.sort(function(a,b){return a.surfaceClearanceMm-b.surfaceClearanceMm});
  return{
    nearestWall:vals[0].wall,
    nearestSurfaceClearanceMm:vals[0].surfaceClearanceMm,
    profile:profile,
    pipeOuterDiameterMm:d,
    pipeOuterRadiusMm:r,
    walls:vals
  };
}
function manifestHas(result,category,componentId){
  return finalList(result&&result.componentManifest).some(function(x){
    if(category&&String(x&&x.category||'')!==String(category))return false;
    if(componentId&&String(x&&x.catalogueComponentId||'')!==String(componentId))return false;
    return true
  });
}
function finalInstallationAssessment(result,context){
  context=context||{};
  var checks=[],c=catalogue(),core=context.core||null,project=context.project||core&&core.analysis&&core.analysis.project||null,
      input=context.input||{},cv=context.contractValidation||null,acc=context.roofAccommodation||null,
      modelId=result&&result.selection&&result.selection.modelId,
      model=c&&c.getModel&&modelId?c.getModel(modelId):null,
      room=core&&core.analysis&&core.analysis.room||project&&project.room||null,
      floor=result&&result.assembly&&result.assembly.floor||null,
      protection=result&&result.assembly&&result.assembly.protection||null,
      flue=result&&result.assembly&&result.assembly.flue||null,
      terminal=flue&&flue.terminal||null,
      terminalValidation=terminal&&(terminal.compliance||terminal.manufacturerValidation)||null,
      opZones=finalList(result&&result.safety&&result.safety.operationalZones),
      clrZones=finalList(result&&result.safety&&result.safety.clearanceZones),
      segs=finalList(flue&&flue.segments),
      single=segs.find(function(x){return String(x&&x.type||'')==='single-wall'})||null,
      insulated=segs.find(function(x){return String(x&&x.type||'')==='insulated-twin-wall'})||null;

  addFinalCheck(checks,'contract','Installation contract',
    cv&&cv.valid===false?'fail':'pass',
    cv&&cv.valid===false?'Resolved installation does not satisfy the Installation Contract.':'Resolved installation satisfies the Installation Contract schema.',
    {contractVersion:contract()&&contract().version||null,errors:clone(cv&&cv.errors||[])});

  if(model&&result&&result.selection){
    var ev=num(result.selection.effectiveVolumeM3,null),min=num(model.sizing&&model.sizing.saunaVolumeMinM3,null),max=num(model.sizing&&model.sizing.saunaVolumeMaxM3,null),
        sizingOk=ev!=null&&min!=null&&max!=null&&ev>=min-1e-9&&ev<=max+1e-9;
    addFinalCheck(checks,'sizing','Heater sizing',sizingOk?'pass':'fail',
      sizingOk?'Selected Harvia model covers the resolved effective sauna volume.':'Resolved effective sauna volume is outside the selected model range.',
      {modelId:modelId,effectiveVolumeM3:ev,minimumM3:min,maximumM3:max});
  }else{
    addFinalCheck(checks,'sizing','Heater sizing','fail','Selected heater model could not be resolved from Heater Catalogue.',{modelId:modelId||null});
  }

  var fit=core&&core.metrics&&core.metrics.clearanceFit||null;
  addFinalCheck(checks,'stove-clearance-envelope','Stove combustible-clearance envelope',
    fit&&fit.fits===true?'pass':fit?'fail':'conditional',
    fit&&fit.fits===true?'Resolved stove/protection clearance envelope fits within the room perimeter.':
      fit?'Resolved stove/protection clearance envelope does not fit within the room perimeter.':
      'Clearance-fit metrics were not available to the final validator.',
    fit||null);

  var protectionItems=finalList(protection&&protection.items),badProtection=protectionItems.filter(function(x){return !x||!x.systemId||!x.system});
  addFinalCheck(checks,'stove-protection','Stove protection system',
    badProtection.length?'fail':'pass',
    badProtection.length?'One or more selected stove-protection items are unresolved.':
      (protectionItems.length?'Selected manufacturer protection system is resolved.':'Unshielded stove configuration is resolved.'),
    {configurationId:protection&&protection.configurationId||null,itemCount:protectionItems.length});

  if(floor&&floor.systemId){
    var floorConditional=String(floor.reason||'').indexOf('provisional')>=0;
    addFinalCheck(checks,'floor-protection','Floor protection',
      floorConditional?'conditional':'pass',
      floorConditional?'Compatible Harvia floor protection is included, but the floor substrate/classification has not yet been confirmed by the Heater Engine.':'Floor-protection selection is explicitly resolved.',
      {systemId:floor.systemId,reason:floor.reason||null});
  }else{
    addFinalCheck(checks,'floor-protection','Floor protection',
      input&&input.floorRequiresProtection===false?'conditional':'conditional',
      input&&input.floorRequiresProtection===false?
        'Floor protection is omitted from an explicit caller declaration; substrate suitability remains a site/design responsibility.':
        'No floor-protection system is resolved; confirm that the finished floor construction permits this.',
      {reason:floor&&floor.reason||null});
  }

  if(opZones.length){
    var op=opZones[0],provisional=String(op.status||'').indexOf('provisional')>=0;
    addFinalCheck(checks,'operational-access','Firebox loading / ash-removal access',
      provisional?'conditional':'pass',
      provisional?'A conservative operational zone is present, but it currently uses Harvia front combustible clearance because no separate access dimension is catalogued.':'A catalogued operational-access zone is resolved.',
      {status:op.status||null,source:op.source||null,frontDepthMm:op.frontDepthMm||null});
  }else{
    addFinalCheck(checks,'operational-access','Firebox loading / ash-removal access','fail',
      'No operational firebox-access zone is present.',null);
  }

  if(single){
    var comp=c&&c.getInstallationComponent&&c.getInstallationComponent(single.componentId||'harvia-wz115100'),
        required=num(comp&&comp.safety&&comp.safety.combustibleRadialClearanceMm,500),
        pipeClear=roomWallClearanceFromVerticalPipe(room,single,clearanceSurfaces(core,project)),
        actual=pipeClear&&pipeClear.nearestSurfaceClearanceMm,
        protectionPlan=flue&&flue.singleWallPlan&&flue.singleWallPlan.protection||null,
        protectedRequired=num(protectionPlan&&protectionPlan.protectedClearanceMm,null),
        fullCoverage=protectionPlan&&protectionPlan.active&&
          num(protectionPlan.coverageLengthMm,0)>=num(protectionPlan.singleWallLengthMm,0)-0.1;

    if(flue&&flue.selection&&flue.selection.tier===3){
      addFinalCheck(checks,'single-wall-radial-clearance','Single-wall smoke-pipe clearance','pass',
        'The insulated chimney starts at the stove outlet, so its insulated casing clearance is verified instead of single-wall pipe clearance.',
        {casingClearanceMm:flue.selection.casingClearanceMm,casingRequiredMm:flue.selection.casingRequiredMm,flueArrangement:'twin-wall'});
    }else if(actual!=null&&actual>=required-0.1){
      addFinalCheck(checks,'single-wall-radial-clearance','Single-wall smoke-pipe clearance','pass',
        'Single-wall smoke pipe satisfies the catalogued unprotected radial clearance to the room perimeter.',
        {actualMinimumMm:actual,requiredMm:required,nearestWall:pipeClear.nearestWall,protectionRequired:false});
    }else if(protectionPlan&&protectionPlan.active&&fullCoverage&&protectedRequired!=null&&actual!=null&&actual>=protectedRequired-0.1){
      addFinalCheck(checks,'single-wall-radial-clearance','Single-wall smoke-pipe clearance','pass',
        'Single-wall smoke pipe satisfies the documented protected clearance using the resolved pipe protector.',
        {
          actualMinimumMm:actual,
          unprotectedRequiredMm:required,
          protectedRequiredMm:protectedRequired,
          nearestWall:pipeClear.nearestWall,
          componentId:protectionPlan.componentId||null,
          manufacturer:protectionPlan.manufacturer||null,
          model:protectionPlan.model||null,
          singleWallLengthMm:protectionPlan.singleWallLengthMm||null,
          coverageLengthMm:protectionPlan.coverageLengthMm||null,
          documentationConfirmed:protectionPlan.documentationConfirmed===true,
          compatibilityConfirmed:protectionPlan.compatibilityConfirmed===true
        });
    }else{
      addFinalCheck(checks,'single-wall-radial-clearance','Single-wall smoke-pipe clearance','fail',
        protectionPlan&&protectionPlan.status==='candidate-compatibility-unconfirmed'?
          'The Jeremias pipe protector is an allowed candidate, but its compatibility with the current Harvia WZ115100 pipe has not yet been confirmed.':
        protectionPlan&&String(protectionPlan.status||'').indexOf('harvia-guard')===0?
          'A single Harvia WZ020130 guard does not provide full documented coverage of this single-wall section; multiple WZ020130 guards are not allowed without Harvia documentation.':
          'The resolved single-wall smoke pipe is inside its unprotected clearance and no fully documented compatible full-length protector is resolved.',
        {
          actualMinimumMm:actual,
          unprotectedRequiredMm:required,
          nearestWall:pipeClear&&pipeClear.nearestWall||null,
          protectionPlan:clone(protectionPlan)
        });
    }
  }else{
    addFinalCheck(checks,'single-wall-radial-clearance','Single-wall smoke-pipe clearance','not-applicable',
      'No single-wall smoke-pipe segment is present.',null);
  }

  if(insulated&&flue&&flue.reserveZone){
    var rr=num(flue.reserveZone.radiusMm,null),od=num(flue.reserveZone.outerDiameterMm,null),cc=num(flue.reserveZone.combustibleClearanceMm,null);
    addFinalCheck(checks,'insulated-chimney-clearance','Insulated chimney clearance',
      rr!=null&&od!=null&&cc!=null&&Math.abs(rr-(od/2+cc))<0.5?'pass':'fail',
      rr!=null&&od!=null&&cc!=null&&Math.abs(rr-(od/2+cc))<0.5?
        'Insulated chimney reserve zone matches the resolved outer casing plus combustible clearance.':
        'Insulated chimney reserve-zone geometry is incomplete or inconsistent.',
      {radiusMm:rr,outerDiameterMm:od,combustibleClearanceMm:cc});
  }else{
    addFinalCheck(checks,'insulated-chimney-clearance','Insulated chimney clearance','fail',
      'Resolved insulated chimney / reserve zone is missing.',null);
  }

  var pens=finalList(flue&&flue.penetrations),ceilPen=pens.find(function(x){return x&&x.hostType==='ceiling'}),roofPen=pens.find(function(x){return x&&x.hostType==='roof'});
  addFinalCheck(checks,'penetrations','Ceiling and roof penetrations',
    ceilPen&&roofPen?'pass':'fail',
    ceilPen&&roofPen?'Ceiling and roof penetrations are both resolved.':'One or more required chimney penetrations are missing.',
    {ceiling:!!ceilPen,roof:!!roofPen});

  var penetrationParts=['harvia-whp-ceiling-flange','harvia-whp-leadthrough-insulation','harvia-whp-leadthrough-protector','harvia-whp-rain-flange'],
      missingPen=penetrationParts.filter(function(id){return !finalList(result&&result.componentManifest).some(function(x){return x&&x.catalogueComponentId===id})});
  addFinalCheck(checks,'penetration-components','Penetration / weathering components',
    missingPen.length?'fail':'pass',
    missingPen.length?'Required penetration/weathering components are missing from the component manifest.':'Required ceiling penetration and roof-weathering components are present in the manifest.',
    {missingComponentIds:missingPen});

  var metal=acc&&acc.plan&&acc.plan.metalRoofCoveringExclusion||acc&&acc.building&&acc.building.roofPenetrationAccommodation&&acc.building.roofPenetrationAccommodation.metalRoofCoveringExclusion||null;
  addFinalCheck(checks,'roof-accommodation','Roof structural/layer accommodation',
    acc&&acc.valid!==false?'pass':'fail',
    acc&&acc.valid!==false?'Canonical heater roof accommodation completed without a blocking error.':'Canonical heater roof accommodation is unavailable or invalid.',
    {version:acc&&acc.version||null,metalRoofClearanceFromFlueEdgeMm:metal&&metal.clearanceFromFlueOuterEdgeMm||null});

  if(terminalValidation){
    var ts=String(terminalValidation.status||terminal&&terminal.validationStatus||terminal&&terminal.manufacturerStatus||''),
        terminalOk=ts==='manufacturer-compliant';
    addFinalCheck(checks,'terminal','Chimney terminal height + manufacturer limits',
      terminalOk?'pass':'fail',
      terminalOk?
        'Terminal assembly satisfies the manufacturer terminal-height rule and remains within the selected chimney manufacturer system limits.':
        'Terminal assembly does not satisfy the manufacturer terminal-height rule and/or the selected chimney manufacturer system limits.',
      {status:ts,authority:terminalValidation.terminalHeightAuthority||terminal&&terminal.terminalHeightAuthority||'manufacturer-catalogue',regionalSpecificationStatus:'deferred'});
  }else{
    addFinalCheck(checks,'terminal','Chimney terminal height + manufacturer limits','fail','No terminal validation result is present.',null);
  }

  var benchOff=project&&project.bench&&project.bench.include===false;
  addFinalCheck(checks,'interior-combustibles','Interior combustible fixtures / benches',
    benchOff?'not-applicable':'conditional',
    benchOff?'Bench system is disabled; no bench-clearance validation is required.':
      'Exact Heater System safety zones are not yet intersected here with authoritative Bench Geometry / other interior combustible fixtures. Keep this as a release check before final manufacturing.',
    {benchIncluded:!benchOff});

  addFinalCheck(checks,'floor-load','Local floor load',
    'conditional',
    'Resolved heater load is available, but local floor-structure capacity remains a project/site structural check.',
    {resolvedInstallationLoadKg:core&&core.metrics&&core.metrics.resolvedInstallationLoadKg||null});

  var counts={pass:0,conditional:0,fail:0,'not-applicable':0};
  checks.forEach(function(x){if(counts[x.status]==null)counts[x.status]=0;counts[x.status]++});
  var overall=counts.fail?'fail':counts.conditional?'conditional':'pass',
      blockers=checks.filter(function(x){return x.status==='fail'}).map(function(x){return x.id}),
      conditions=checks.filter(function(x){return x.status==='conditional'}).map(function(x){return x.id});

  return{
    schema:'thermoaspen-heater-final-validation-v1',
    version:'1.0.0',
    engineVersion:VERSION,
    stage:STAGE,
    overallStatus:overall,
    releaseReady:overall==='pass',
    geometryResolved:true,
    counts:counts,
    blockerCheckIds:blockers,
    conditionalCheckIds:conditions,
    checks:checks,
    notes:[
      'A failing final check does not suppress resolved stove/chimney geometry; it blocks release readiness only.',
      'Conditional checks identify explicit assumptions or site/design inputs that are not yet authoritative.',
      'The Heater System Engine remains the authority for resolved heater/flue geometry; this validator does not reconstruct it.'
    ]
  };
}


function validateEnvironment(){
  var errors=[],warnings=[],c=catalogue(),k=contract();
  if(!c)issue(errors,'HEATER_CATALOGUE_MISSING','environment','ThermoAspenHeaterCatalogue is required.');
  if(!k)issue(errors,'HEATER_INSTALLATION_CONTRACT_MISSING','environment','ThermoAspenHeaterInstallationContract is required.');
  if(!buildingEngine())issue(errors,'BUILDING_ENGINE_MISSING','environment','ThermoAspenBuildingGeometryEngine is required for heater/flue resolution.');
  if(!roofAccommodation())issue(errors,'ROOF_ACCOMMODATION_ENGINE_MISSING','environment','ThermoAspenHeaterRoofAccommodationV1 is required for Stage 2F.');
  if(c&&typeof c.validate==='function'){
    var cv=c.validate();if(!cv.valid)issue(errors,'HEATER_CATALOGUE_INVALID','environment.catalogue','Heater Catalogue validation failed.',cv.errors);
  }
  if(k&&typeof k.validateEnvironment==='function'){
    var kv=k.validateEnvironment();if(!kv.valid)issue(errors,'HEATER_INSTALLATION_CONTRACT_INVALID','environment.contract','Heater Installation Contract environment validation failed.',kv.errors);
  }
  return{
    valid:errors.length===0,
    engineVersion:VERSION,
    stage:STAGE,
    catalogueVersion:c&&c.version||null,
    installationContractVersion:k&&k.version||null,
    roofAccommodationVersion:roofAccommodation()&&roofAccommodation().version||null,
    terminalAuthority:'manufacturer',
    regionalSpecificationStatus:'deferred',
    coordinateSystemId:COORD,
    errors:errors,
    warnings:warnings
  };
}

/* Fuel-aware façade. This lives in the same Heater System Engine and uses the same
 * Catalogue, coordinate conventions, resolved clearance-zone schema and project intent.
 * For wood, all original v1.6.3 resolution and flue logic stays unchanged.
 * Electric is deliberately conditional until mounting/bench/control details are verified.
 */
function requestedFuel(input){
 var a=analyse(input||{}),p=a&&a.project||{},h=input&&input.heaterIntent||p.heater||{};
 if(h.fuelMode==='manual'&&(h.fuel==='wood'||h.fuel==='electric'))return h.fuel;
 return a&&a.valid&&a.effectiveVolumeM3>9?'wood':'electric';
}
function electricCandidates(input){
 var a=analyse(input),c=catalogue(),vv=a&&a.effectiveVolumeM3||0,hh=a&&a.room&&a.room.heightMm||0;
 var all=(c&&c.listModels?c.listModels():[]).filter(function(m){return m.identity&&m.identity.fuel==='electric'}).map(function(m){
 var s=m.sizing||{},vol=vv>=s.saunaVolumeMinM3&&vv<=s.saunaVolumeMaxM3,height=hh>=s.minimumRoomHeightMm;
 return{modelId:m.id,model:m.identity.model,manufacturer:m.identity.manufacturer,nominalOutputKw:s.nominalOutputKw,
 volumeRangeM3:{min:s.saunaVolumeMinM3,max:s.saunaVolumeMaxM3},volumeOk:vol,heightOk:height,suitable:vol&&height};
 });
 var suitable=all.filter(function(x){return x.suitable}).sort(function(x,y){return x.nominalOutputKw-y.nominalOutputKw});
 return{valid:a.valid&&suitable.length>0,analysis:a,candidates:all,suitableCandidates:suitable,
 errors:a.errors.concat(suitable.length?[]:[{code:'NO_ELECTRIC_MODEL_IN_RANGE',message:'No verified volume-range electric heater is catalogued for this sauna.'}]),warnings:a.warnings};
}
function electricCore(input){
 input=input||{};
 var mc=electricCandidates(input),errors=(mc.errors||[]).slice(),warnings=(mc.warnings||[]).slice();
 if(!mc.analysis||!mc.analysis.valid)return{valid:false,fuel:'electric',errors:errors,warnings:warnings,analysis:mc.analysis};
 var p=mc.analysis.project,h=Object.assign({},p&&p.heater||{},input.heaterIntent||{}),room=mc.analysis.room,
 c=catalogue(),chosen=null,placement=null,ids=mc.suitableCandidates.map(function(x){return x.modelId});
  var preferred=h.preferredModelId||(h.selectionMode==='manual'&&h.modelId?h.modelId:null);
  if(preferred&&ids.indexOf(preferred)>=0)ids=[preferred].concat(ids.filter(function(id){return id!==preferred}));
 for(var i=0;i<ids.length;i++){
   var m=c.getModel(ids[i]);
   if(!m||!m.identity||m.identity.fuel!=='electric')continue;
   if(mc.analysis.effectiveVolumeM3<m.sizing.saunaVolumeMinM3||mc.analysis.effectiveVolumeM3>m.sizing.saunaVolumeMaxM3||room.heightMm<m.sizing.minimumRoomHeightMm)continue;
   var userPlacement=Object.assign({wall:'front',offsetMm:300,rotationDeg:0},h.placement||{});
   var intent={placement:userPlacement,protection:{mode:'auto',configurationId:null}};
   var z=chooseProtectionAndPlacement(m,room,intent,warnings,input);
   if(z.valid){chosen=m;placement=z;break;}
 }
 if(!chosen){issue(errors,'NO_ELECTRIC_INSTALLATION_FITS','heater','No electric heater fits the room perimeter and its recorded manufacturer safety clearances.');
   return{valid:false,fuel:'electric',errors:errors,warnings:warnings,analysis:mc.analysis,candidates:mc.candidates};}
 placement.room=room;
 var geom=stoveGeometry(chosen,placement),isWall=chosen.physical.installationLocation==='wall',heightOffset=isWall?num(chosen.physical.minimumMountHeightMm,80):0;
 // Wall-mounted electric heater: shift body and safety zones above finished floor.
 geom.transform.originMm.y+=heightOffset;
 function raise(box){if(box&&box.min&&box.max){box.min.y+=heightOffset;box.max.y+=heightOffset}}
 raise(geom.body.aabbMm);geom.clearanceZones.forEach(function(z){raise(z.aabbMm)});
 var mountWarning={code:'ELECTRIC_MOUNTING_UNVERIFIED',path:'assembly.stove.mount',message:'Electric heater mounting, sensor/control position and optional protective accessory details must be reviewed against its manufacturer manual. Drawing remains conditional.'};
 if(placement.fit && !placement.fit.clearancesComplete){
   issue(warnings,'ELECTRIC_CLEARANCE_DATUM_UNVERIFIED','assembly.stove.clearances',
     'The manufacturer rear wall-mount datum is unknown; any provisional layout clearance is NOT approved.',
     {faces:placement.fit.unverifiedClearanceFaces});
 }
 warnings.push(mountWarning);
 var stove={id:h.id||'heater.01',modelId:chosen.id,wall:placement.wall,
   requestedOffsetMm:placement.requestedOffsetMm,resolvedOffsetMm:placement.resolvedOffsetMm,
   adjusted:placement.adjusted,wallGapMm:placement.wallGapMm,protectionConfigurationId:placement.configuration.id,geometry:geom,
   installationLocation:chosen.physical.installationLocation,mountingHeightMm:heightOffset};
 var cands=mc.suitableCandidates.map(function(q){return q.modelId});
 return{valid:errors.length===0,engineVersion:VERSION,stage:STAGE,contractComplete:false,
   fuel:'electric',installationStatus:'conditional-manufacturer-review',catalogueVersion:c.version,
   coordinateSystemId:COORD,units:UNITS,analysis:mc.analysis,candidates:mc.candidates,
   selection:{fuel:'electric',modelId:chosen.id,manufacturer:'Harvia',model:chosen.identity.model,
      nominalOutputKw:chosen.sizing.nominalOutputKw,effectiveVolumeM3:mc.analysis.effectiveVolumeM3,
      estimatedElectricDemandKw:mc.analysis.effectiveVolumeM3,
      candidateModelIds:cands,reason:preferred&&chosen.id===preferred?'preferred-model-compliant':'automatic-suitable-model'},
   assembly:{stove:stove,protection:{configurationId:placement.configuration.id,items:[]},floor:{systemId:null,reason:'electric-heater-manufacturer-installation'},flue:null,
      controls:{type:chosen.controls&&chosen.controls.type||'manufacturer-defined',sensorAndWiring:'manufacturer-electrician-confirmation-required'}},
   safety:{clearanceZones:geom.clearanceZones,operationalZones:[],flueClearanceZones:[]},
   metrics:{clearanceFit:clone(placement.fit)},
   buildingRequirements:[{id:'heater.01.requirement.electric-mount',type:'reserve-clear-zone',status:'required',priority:'heater-system',geometryRef:'heater.01.body',hostRef:chosen.physical.installationLocation==='wall'?'interior-wall':'finished-floor',affectedMemberIds:[],note:'Manufacturer mounting/standing-footprint, controller and sensor placement require verification.'}],
   errors:errors,warnings:warnings};
}

/* v1.7.3 — read-only inspection of an EXACT, user-placed heater position.
 * Safety acceptance and visualisation are separate. A failed installation must
 * not erase manufacturer distances or make the physical heater undraggable.
 * Invalid previews are NEVER valid installations and cannot be used to approve
 * construction/manufacturing. Clearance values ALWAYS come from the catalogue
 * configuration, including its unknown/null manufacturer facts.
 */
function inspectPlacement(input){
 input=input||{};
 var a=analyse(input),cat=catalogue(),fuel=requestedFuel(input);
 if(!a.valid||!cat)return{valid:false,status:'unavailable',fuel:fuel,errors:a.errors||[],reason:'Heater project or catalogue unavailable.'};
 var p=a.project||{},h=Object.assign({},p.heater||{},input.heaterIntent||{}),room=a.room;
 var specified=h.selectionMode==='manual'&&h.modelId?cat.getModel(h.modelId):null;
 var all=fuel==='electric'?electricCandidates(input):modelCandidates(input);
 var ids=specified?[specified.id]:(all.suitableCandidates||[]).map(function(x){return x.modelId});
 // If a model is not suitable for this volume we still need to show it and its
 // manufacturer dimensions as an invalid selection, rather than hide the stove.
 if(!ids.length&&all.candidates&&all.candidates.length)ids=[all.candidates[0].modelId];
 var model=cat.getModel(ids[0]);
 if(!model)return{valid:false,status:'unavailable',fuel:fuel,errors:all.errors||[],reason:'No heater model can be displayed.'};
 var wall=h.placement&&h.placement.wall||'front';
 if(['front','back','left','right'].indexOf(wall)<0)wall='front';
 var offset=num(h.placement&&h.placement.offsetMm,300);
 var width=num(model.physical&&model.physical.widthMm,0);
 var span=(wall==='front'||wall==='back')?room.lengthMm:room.widthMm;
 offset=clamp(offset,0,Math.max(0,span-width));
 var core=unifiedCore(input);
 if(core&&core.valid){
   return{valid:true,status:core.installationStatus||'resolved',fuel:fuel,modelId:core.selection.modelId,
     model:cat.getModel(core.selection.modelId),position:{wall:wall,offsetMm:offset,wallGapMm:core.assembly.stove.wallGapMm},
     geometry:core.assembly.stove.geometry,clearanceZones:core.safety.clearanceZones||[],
     operationalZones:core.safety.operationalZones||[],clearances:core.metrics&&core.metrics.clearanceFit&&core.metrics.clearanceFit.clearances||null,
     protectionItems:core.assembly.protection&&core.assembly.protection.items||[],
     configurationId:core.assembly.stove.protectionConfigurationId,core:core,errors:[],reason:null};
 }
 var configs=fuel==='wood'?protectionOptions(model):[{id:model.id+'-unshielded-preview',installedProtection:[],effectiveClearancesFromHeaterBody:{combustible:clone(model.clearances.base.combustible)}}];
 configs=configs.filter(function(cfg){return legendPanelsFaceOnlyWalls(model,room,wall,offset,cfg)});
 var options=configs.map(function(cfg){
   var fit=fitForConfiguration(room,model,wall,cfg);
   var mode=h.placement&&h.placement.wallStandoffMode,opts=rearStandoffValues(model,room,wall,offset);
   if(mode==='shielded'&&(!rearShieldOf(cfg)||opts.shieldedMm==null||Math.abs(fit.clearances.rearMm-opts.shieldedMm)>.01))return null;
   if(mode==='unshielded'&&(rearShieldOf(cfg)||Math.abs(fit.clearances.rearMm-opts.unshieldedMm)>.01))return null;
   var gap=mode==='shielded'?opts.shieldedMm:mode==='unshielded'?opts.unshieldedMm:
     h.placement&&h.placement.wallGapMm!=null?Number(h.placement.wallGapMm):fit.clearances.rearMm;
   var perp=wall==='front'||wall==='back'?room.widthMm:room.lengthMm;
   var validGap=isFinite(gap)&&gap>=fit.clearances.rearMm-.01&&gap+model.physical.depthMm+fit.clearances.frontMm<=perp+.01;
   var status=candidateLayoutStatus(model,room,wall,offset,fit,input,gap);
   var tooNear=offset<fit.minimumBodyOffsetMm-.01||offset>fit.maximumBodyOffsetMm+.01||!validGap;
   var shortage=Math.max(0,fit.minimumBodyOffsetMm-offset)+
     Math.max(0,offset-fit.maximumBodyOffsetMm)+
     Math.max(0,fit.required.perpendicularMm-((wall==='front'||wall==='back')?room.widthMm:room.lengthMm));
   var bad=(fit.fits?0:1)+(tooNear?1:0)+status.conflicts.length;
   return{cfg:cfg,fit:fit,gap:gap,status:status,bad:bad,shortageMm:shortage,offsetOutOfRange:tooNear,
     pieces:(cfg.installedProtection||[]).length};
 }).filter(Boolean).sort(function(x,y){return x.bad-y.bad||x.shortageMm-y.shortageMm||
     x.fit.clearances.rearMm-y.fit.clearances.rearMm||x.pieces-y.pieces});
 var best=options[0];
 if(!best)return{valid:false,status:'unavailable',fuel:fuel,modelId:model.id,model:model,errors:core&&core.errors||[],reason:'No catalogue clearance data.'};
 var placement={room:room,wall:wall,resolvedOffsetMm:offset,fit:best.fit,wallGapMm:best.gap};
 var geo=stoveGeometry(model,placement);
 if(fuel==='electric'){
   var mount=model.physical.installationLocation==='wall'?num(model.physical.minimumMountHeightMm,80):0;
   geo.transform.originMm.y+=mount;
   function raise(b){if(b&&b.min&&b.max){b.min.y+=mount;b.max.y+=mount}}
   raise(geo.body.aabbMm);geo.clearanceZones.forEach(function(z){raise(z.aabbMm)});
 }
 var operational=fuel==='wood'?operationalAccessZones(model,placement,geo,[]):[];
 var parts=(best.cfg.installedProtection||[]).map(function(q){return{systemId:q.systemId,position:q.position}});
 var reasons=[];
 if(!best.fit.fits||best.offsetOutOfRange)reasons.push('Insufficient manufacturer clearance to room boundaries');
 if(best.status.conflicts.length)reasons.push(formatPlacementConflict(best.status.conflicts[0])||
   'Collision with '+best.status.conflicts[0].id);
 if(core&&core.errors&&core.errors.length)reasons.push(core.errors[0].message||core.errors[0].code);
 if(!reasons.length)reasons.push('Installation not resolved; review heater suitability');
 return{valid:false,status:'invalid-placement-preview',fuel:fuel,modelId:model.id,model:model,
   position:{wall:wall,offsetMm:offset,wallGapMm:best.gap},geometry:geo,clearanceZones:geo.clearanceZones,
   operationalZones:operational,clearances:clone(best.fit.clearances),protectionItems:parts,
   configurationId:best.cfg.id,core:core,errors:core&&core.errors||[],
   reason:reasons.join('. '),firstFailure:best.status.conflicts[0]||null,conflicts:best.status.conflicts,
   warning:'REFERENCE ONLY: manufacturer clearance geometry at this position; the installation is NOT approved.'};
}

/* v1.7.5 — voluntary, drag-preview-only correction of an entrance BODY collision.
 * Returns the closest same-wall, manufacturer-permitted position at which the
 * physical stove clears the door. Shields are evaluated individually and in
 * approved combinations. Never relocates in resolveCore/Construction/C&M.
 * The Floor Plan must visibly draw and commit the returned position; otherwise
 * the input is unchanged and remains invalid.
 */
function suggestDoorSafePlacement(input){
 input=input||{};
 var analysis=analyse(input),cat=catalogue();
 if(!analysis.valid||!cat)return{found:false,reason:'Heater analysis unavailable'};
 var p=analysis.project||{},h=normaliseHeaterIntent(p,input),room=analysis.room,
     wall=h.placement&&h.placement.wall||'front',requested=num(h.placement&&h.placement.offsetMm,null),
     fuel=requestedFuel(input),mc=unifiedCandidates(input),maxMove=num(input.maximumNudgeMm,250);
 if(requested===null||!isFinite(requested)||!mc.valid)return{found:false,reason:'Placement or model unavailable'};
 var models=h.selectionMode==='manual'&&h.modelId?[h.modelId]:(mc.suitableCandidates||[]).map(function(q){return q.modelId});
 var reservations=(input.layoutReservations||[]).slice(),automatic=projectEntranceReservation(input,room);
 if(automatic&&!reservations.some(function(r){return r&&r.id==='entrance-clearance'}))reservations.push(automatic);
 var boxes=reservations.map(layoutBox).filter(Boolean),ranked=[],bodyConflict=false;
 models.forEach(function(modelId,modelRank){
   var model=cat.getModel(modelId);if(!model)return;
   var opts=fuel==='wood'?protectionOptions(model):[
     {id:model.id+'-unshielded-electric',installedProtection:[],effectiveClearancesFromHeaterBody:{combustible:clone(model.clearances.base.combustible)}}];
   opts.forEach(function(cfg){
     var fit=fitForConfiguration(room,model,wall,cfg);
     if(!fit.fits)return;
     var mode=h.placement&&h.placement.wallStandoffMode,opts=rearStandoffValues(model,room,wall,requested);
     if(mode==='shielded'&&(!rearShieldOf(cfg)||opts.shieldedMm==null||Math.abs(fit.clearances.rearMm-opts.shieldedMm)>.01))return null;
     if(mode==='unshielded'&&(rearShieldOf(cfg)||Math.abs(fit.clearances.rearMm-opts.unshieldedMm)>.01))return null;
     var gap=mode==='shielded'?opts.shieldedMm:mode==='unshielded'?opts.unshieldedMm:
       h.placement&&h.placement.wallGapMm!=null?Number(h.placement.wallGapMm):fit.clearances.rearMm;
     var perp=wall==='front'||wall==='back'?room.widthMm:room.lengthMm;
     if(!(isFinite(gap)&&gap>=fit.clearances.rearMm-.01&&gap+model.physical.depthMm+fit.clearances.frontMm<=perp+.01))return;
     var initial={room:room,wall:wall,resolvedOffsetMm:requested,fit:fit,wallGapMm:gap},b=layoutBox(stoveGeometry(model,initial).body.aabbMm);
     var clashes=boxes.filter(function(box){return layoutOverlap(b,box)});
     if(clashes.length)bodyConflict=true;
     if(!clashes.length)return;
     var w=num(model.physical.widthMm,0),axis=wall==='front'||wall==='back'?'x':'z',offsets=[requested];
     boxes.forEach(function(box){
       offsets.push(box[axis+'1']-w,box[axis+'2']);
     });
     offsets.forEach(function(off){
       if(off<fit.minimumBodyOffsetMm-.01||off>fit.maximumBodyOffsetMm+.01)return;
       if(!legendPanelsFaceOnlyWalls(model,room,wall,off,cfg))return;
       if(Math.abs(off-requested)>maxMove+.01)return;
       var check=candidateLayoutStatus(model,room,wall,off,fit,input,gap);
       if(!check.valid||shieldBetweenBenchAndStove(model,room,wall,off,fit,cfg,input,gap).length)return;
       ranked.push({modelId:model.id,wall:wall,offsetMm:Math.round(off),wallGapMm:gap,
         configurationId:cfg.id,shieldPositions:(cfg.installedProtection||[]).map(function(v){return v.position}),
         movedByMm:Math.round(off-requested),absMoveMm:Math.abs(off-requested),
         partCount:(cfg.installedProtection||[]).length,modelRank:modelRank});
     });
   });
 });
 ranked.sort(function(a,b){return a.absMoveMm-b.absMoveMm||a.partCount-b.partCount||a.modelRank-b.modelRank});
 return ranked.length&&bodyConflict?{found:true,placement:{wall:ranked[0].wall,offsetMm:ranked[0].offsetMm,wallGapMm:ranked[0].wallGapMm},
   modelId:ranked[0].modelId,configurationId:ranked[0].configurationId,
   shieldPositions:ranked[0].shieldPositions,movedByMm:ranked[0].movedByMm,
   reason:'Snapped stove body just outside the entrance keep-clear area'}:
   {found:false,reason:bodyConflict?'No nearby manufacturer-permitted position clears the entrance':'No entrance body collision requiring relocation'};
}

// Public resolutions honour the saved position. Never silently shift a user-dragged
// heater to make a drawing look valid; relocation must come from the Floor Plan.
function exactPositionInput(input){
 var out=Object.assign({},input||{});
 if(out.strictPlacement!==false)out.strictPlacement=true;
 return out;
}
function unifiedCandidates(input){return requestedFuel(input)==='electric'?electricCandidates(input):modelCandidates(input)}
function unifiedRecommend(input){
 input=exactPositionInput(input);
 if(requestedFuel(input)!=='electric')return recommend(input);
 var c=electricCore(input),r=c&&c.selection;
 return{valid:c.valid,analysis:c.analysis,recommendation:r?{modelId:r.modelId,manufacturer:r.manufacturer,model:r.model,
   reason:r.reason,nominalOutputKw:r.nominalOutputKw,protectionConfigurationId:c.assembly.protection.configurationId}:null,
   candidates:c.candidates||[],errors:c.errors||[],warnings:c.warnings||[],fuel:'electric'};
}
function unifiedCore(input){input=exactPositionInput(input);return requestedFuel(input)==='electric'?electricCore(input):resolveCore(input)}
function electricInstallation(input){
 var core=electricCore(input);if(!core.valid)return{valid:false,core:core,errors:core.errors,warnings:core.warnings};
 var result={schemaVersion:1,heaterId:core.assembly.stove.id,catalogueVersion:core.catalogueVersion,
   engineVersion:VERSION,coordinateSystemId:COORD,units:UNITS,selection:clone(core.selection),
   assembly:clone(core.assembly),safety:clone(core.safety),buildingRequirements:clone(core.buildingRequirements),
   componentManifest:[{instanceId:'heater.01.electric',catalogueComponentId:core.selection.modelId,category:'stove',quantity:1}],
   installationSequence:[{id:'heater.01.mount',order:1,title:'Mount electric sauna heater',componentInstanceIds:['heater.01.electric'],geometryRefs:['heater.01.body'],buildingRequirementIds:[],notes:['Follow model-specific Harvia mounting, control and sensor manual.']}],
   validation:{valid:null,status:'conditional-manufacturer-review',errors:[],warnings:clone(core.warnings)}};
 var k=contract(),cv=k&&k.validateResolvedInstallation?k.validateResolvedInstallation(result):{valid:true,errors:[],warnings:[]};
 result.validation={valid:cv.valid,status:'conditional-manufacturer-review',contractValidation:cv,
   releaseReady:false,checks:[{id:'mount-review',status:'pending',message:'Mounting, benches/guards, sensor and electrical connection require manufacturer-manual verification.'}]};
 return{valid:cv.valid,core:core,result:result,finalAssessment:result.validation,errors:cv.errors||[],warnings:core.warnings,
   installationStatus:'conditional-manufacturer-review'};
}
function unifiedInstallation(input){input=exactPositionInput(input);return requestedFuel(input)==='electric'?electricInstallation(input):resolveInstallation(input)}

/* v1.7.7 — manufacturer-approved WALL shielding alternatives for bench layout
 * optimisation. A preview is NOT an approved installation: callers must check
 * the selected bench geometry with inspectPlacement/resolveCore afterwards.
 * Never derive protection dimensions from SVG or old stove constants. */
function previewPlacementConfigurations(input){
 input=input||{};
 var a=analyse(input),cat=catalogue(),fuel=requestedFuel(input);
 if(!a||!a.valid||!cat)return{valid:false,options:[],errors:a&&a.errors||[]};
 var project=a.project||{},h=Object.assign({},project.heater||{},input.heaterIntent||{}),
     preferredId=h.preferredModelId||(h.selectionMode==='manual'?h.modelId:null),
     preferred=preferredId&&cat.getModel?cat.getModel(preferredId):null,
     probe=inspectPlacement(input),fuelWanted=requestedFuel(input),
     sizing=preferred&&preferred.sizing||{},
     preferredSized=!!(preferred&&preferred.identity&&preferred.identity.fuel===fuelWanted&&
       a.effectiveVolumeM3>=num(sizing.saunaVolumeMinM3,Infinity)&&
       a.effectiveVolumeM3<=num(sizing.saunaVolumeMaxM3,-Infinity)&&
       a.room.heightMm>=num(sizing.minimumRoomHeightMm,Infinity)),
     model=preferredSized?preferred:probe&&probe.model||null;
 if(!model||!model.physical)return{valid:false,options:[],errors:[{code:'HEATER_MODEL_UNRESOLVED'}]};
 var wall=h.placement&&h.placement.wall||'front',offset=num(h.placement&&h.placement.offsetMm,300),room=a.room;
 if(['front','back','left','right'].indexOf(wall)<0)wall='front';
 var configs=fuel==='wood'?protectionOptions(model):[{id:model.id+'-electric-base',
   installedProtection:[],effectiveClearancesFromHeaterBody:{combustible:clone(model.clearances.base.combustible)}}];
 var options=configs.filter(function(cfg){return legendPanelsFaceOnlyWalls(model,room,wall,offset,cfg)}).map(function(cfg){
   var fit=fitForConfiguration(room,model,wall,cfg),mode=h.placement&&h.placement.wallStandoffMode,
     choices=rearStandoffValues(model,room,wall,offset),
     eligible=mode==='shielded'?rearShieldOf(cfg)&&choices.shieldedMm!=null&&Math.abs(fit.clearances.rearMm-choices.shieldedMm)<=.01:
       mode==='unshielded'?!rearShieldOf(cfg)&&Math.abs(fit.clearances.rearMm-choices.unshieldedMm)<=.01:true,
     gap=mode==='shielded'?choices.shieldedMm:mode==='unshielded'?choices.unshieldedMm:
       h.placement&&h.placement.wallGapMm!=null?Number(h.placement.wallGapMm):fit.clearances.rearMm,
     perp=wall==='front'||wall==='back'?room.widthMm:room.lengthMm,
     inBounds=fit.fits&&isFinite(gap)&&gap>=fit.clearances.rearMm-.01&&gap+model.physical.depthMm+fit.clearances.frontMm<=perp+.01&&
     offset>=fit.minimumBodyOffsetMm-.01&&offset<=fit.maximumBodyOffsetMm+.01;
   var g=stoveGeometry(model,{room:room,wall:wall,resolvedOffsetMm:offset,fit:fit,wallGapMm:gap});
   if(fuel==='electric'){
     var dy=num(model.physical.minimumMountHeightMm,80);
     function rise(box){if(box&&box.min&&box.max){box.min.y+=dy;box.max.y+=dy}}
     g.transform.originMm.y+=dy;rise(g.body.aabbMm);
     rise(g.clearanceEnvelope&&g.clearanceEnvelope.aabbMm);
     (g.clearanceZones||[]).forEach(function(z){rise(z.aabbMm)});
   }
   var space=candidateLayoutStatus(model,room,wall,offset,fit,input,gap);
   if(shieldBetweenBenchAndStove(model,room,wall,offset,fit,cfg,input,gap).length)space.valid=false;
   return{eligible:eligible,configurationId:cfg.id,modelId:model.id,manufacturer:cat.version,
     installedProtection:clone(cfg.installedProtection||[]),partCount:(cfg.installedProtection||[]).length,
     withinRoom:inBounds,doorBodyClear:space.valid,doorConflicts:clone(space.conflicts),
     clearances:clone(fit.clearances),wallGapMm:gap,geometry:g};
 }).filter(function(x){return x.eligible&&x.withinRoom&&x.doorBodyClear});
 return{valid:options.length>0,modelId:model.id,fuel:fuel,options:options,errors:[]};
}

/* 1.8.1 — per-task memo (see header). */
var POLICY_CACHE={cat:null,ver:null,value:null};
function sizingPolicies(c){
  if(!c||!c.getData)return null;
  if(POLICY_CACHE.cat!==c||POLICY_CACHE.ver!==c.version){var d=c.getData();POLICY_CACHE={cat:c,ver:c.version,value:d&&d.policies&&d.policies.sizingPolicies||null}}
  return POLICY_CACHE.value?clone(POLICY_CACHE.value):null
}
var MEMO={map:null,hits:0,misses:0},MEMO_CLONE=typeof structuredClone==='function'?structuredClone:null;
function memoStateKey(){
  var S=W.ThermoAspenConstructionSpecV1,C=W.ThermoAspenHeaterCatalogue,k='';
  try{k+=JSON.stringify(S&&S.getCurrentRequest?S.getCurrentRequest():null)+'|'+(S&&S.getMaxStudSpacingMm?S.getMaxStudSpacingMm():'')}catch(e){}
  return k+'|'+(C&&C.version||'')
}
function memoCopy(v,shared){
  if(!v||typeof v!=='object'||Array.isArray(v))return MEMO_CLONE(v);
  var o={};Object.keys(v).forEach(function(k){o[k]=shared&&shared.indexOf(k)>=0?v[k]:MEMO_CLONE(v[k])});return o
}
function memoTask(name,fn,shared){
  if(!MEMO_CLONE)return fn;
  return function(input){
    if(arguments.length!==1)return fn.apply(this,arguments);
    var key;try{key=name+'\u0001'+JSON.stringify(input)+'\u0001'+memoStateKey()}catch(e){return fn.apply(this,arguments)}
    if(!MEMO.map){MEMO.map=new Map();var clear=function(){MEMO.map=null};if(typeof queueMicrotask==='function')queueMicrotask(clear);else Promise.resolve().then(clear)}
    if(MEMO.map.has(key)){try{var hit=memoCopy(MEMO.map.get(key),shared);MEMO.hits++;return hit}catch(e){MEMO.map.delete(key)}}
    MEMO.misses++;
    var r=fn.apply(this,arguments);
    try{MEMO.map.set(key,memoCopy(r,shared))}catch(e2){}
    return r
  }
}

var API={
  version:VERSION,
  stage:STAGE,
  coordinateSystemId:COORD,
  units:UNITS,

  validateEnvironment:validateEnvironment,
  readCurrentProject:currentProject,
  analyse:analyse,
  analyseCurrent:function(options){options=clone(options||{});options.project=currentProject();return analyse(options)},
  listCandidates:unifiedCandidates,
  listCurrentCandidates:function(options){options=clone(options||{});options.project=currentProject();return unifiedCandidates(options)},
  recommend:unifiedRecommend,
  recommendCurrent:function(options){options=clone(options||{});options.project=currentProject();return unifiedRecommend(options)},
  resolveCore:unifiedCore,
  inspectPlacement:memoTask('inspectPlacement',inspectPlacement),
  wallStandoffOptions:wallStandoffOptions,
  previewPlacementConfigurations:previewPlacementConfigurations,
  suggestDoorSafePlacement:suggestDoorSafePlacement,
  resolveCurrentCore:function(options){options=clone(options||{});options.project=currentProject();return unifiedCore(options)},

  resolveInstallation:memoTask('resolveInstallation',unifiedInstallation,['roofAccommodation','building']),
  resolveCurrentInstallation:function(options){options=clone(options||{});options.project=currentProject();return unifiedInstallation(options)},
  resolveCurrentRoofAccommodation:function(options){
    var x=clone(options||{});x.project=currentProject();
    if(requestedFuel(x)==='electric')return{valid:true,status:'not-applicable',reason:'Electric heater has no flue/roof penetration.'};
    var r=resolveInstallation(x);
    return r&&r.roofAccommodation||{valid:false,errors:r&&r.errors||[{code:'INSTALLATION_UNRESOLVED',message:'Heater installation could not be resolved.'}]};
  },
  resolveCeilingAccommodation:function(ceilingLayer,options){
    var A=roofAccommodation(),x=clone(options||{}),r;
    if(requestedFuel(x)==='electric')return{valid:true,status:'not-applicable',reason:'Electric heater has no chimney ceiling penetration.'};
    if(!A||typeof A.applyCeilingLayer!=='function')return{valid:false,errors:[{code:'ROOF_ACCOMMODATION_ENGINE_MISSING',message:'Ceiling accommodation API is unavailable.'}]};
    r=resolveInstallation(x);
    if(!r||!r.valid||!r.result)return{valid:false,errors:r&&r.errors||[{code:'INSTALLATION_UNRESOLVED',message:'Heater installation could not be resolved.'}]};
    return A.applyCeilingLayer(ceilingLayer,r.result);
  },

  validateResolvedInstallationFinal:function(resolved,options){
    options=options||{};
    var r=resolved&&resolved.result?resolved.result:resolved;
    if(!r)return{valid:false,errors:[{code:'FINAL_VALIDATION_INPUT_MISSING',message:'Resolved installation is required.'}]};
    if(r.selection&&r.selection.fuel==='electric')return{valid:true,releaseReady:false,assessment:r.validation};
    var q=finalInstallationAssessment(r,{
      core:options.core||resolved&&resolved.core||null,
      project:unwrapProject(options.project)||options.project||resolved&&resolved.core&&resolved.core.analysis&&resolved.core.analysis.project||currentProject(),
      input:options,
      contractValidation:r.validation&&r.validation.contractVersion?{valid:true,errors:[],warnings:[]}:null,
      roofAccommodation:options.roofAccommodation||resolved&&resolved.roofAccommodation||null,
      building:options.building||null
    });
    return{valid:q.overallStatus!=='fail',assessment:q}
  },
  validateCurrentInstallationFinal:function(options){
    var x=clone(options||{});x.project=currentProject();
    var r=unifiedInstallation(x);
    if(requestedFuel(x)==='electric')return{valid:!!(r&&r.valid),releaseReady:false,assessment:r&&r.finalAssessment,resolved:r};
    return r&&r.finalValidation?{valid:r.finalValidation.overallStatus!=='fail',assessment:clone(r.finalValidation),resolved:r}:
      {valid:false,errors:r&&r.errors||[{code:'INSTALLATION_UNRESOLVED',message:'Heater installation could not be resolved.'}]}
  },

  getSingleWallProtectionPolicy:function(){return{
    version:'1.0.0',
    multipleHarviaWZ020130Allowed:false,
    manufacturerGuardRule:'WZ020130 may be used only within the coverage and arrangement supported by Harvia documentation; serial stacking is not inferred.',
    documentedExternalProtectionAllowed:true,
    externalProtectionRequirements:[
      'documentation confirms the protector is suitable for the actual smoke-pipe system/material/diameter',
      'documentation states the resulting combustible clearance',
      'protector covers the complete single-wall section requiring reduced clearance',
      'installation follows the protector manufacturer documentation'
    ],
    candidate:'jeremias-ferro1961-dn115-candidate'
  }},
  getTerminalHeightPolicy:function(systemId){
    var errs=[],sys=catalogue()&&catalogue().getFlueSystem&&catalogue().getFlueSystem(systemId||'harvia-whp1500'),
        req=sys?flueSystemRequirements(sys,errs):null;
    if(!req)return{valid:false,errors:errs.length?errs:[{code:'FLUE_SYSTEM_NOT_FOUND',message:'No flue system catalogue record is available.'}]};
    return{
      valid:true,
      authority:'manufacturer',
      source:'flue-system catalogue record '+sys.id,
      ruleId:req.ruleId,
      verificationStatus:req.ruleVerificationStatus,
      baseRiseAboveRoofAtFlueCentrelineMm:req.baseRiseMm,
      additionalRisePerMetreFromReferenceMm:req.perMetreMm,
      pitchedGableReference:'ridge',
      monopitchReference:'high-roof-edge',
      regionalSpecification:{status:'deferred',applied:false},
      note:'Manufacturer rule from the catalogue. Regional rules are deferred until the Specifications section exists.'
    };
  },
  getManufacturerTerminalPolicy:function(){return{
    authority:'manufacturer',
    source:'selected flue-system catalogue record',
    appliesTo:'flue system geometry, terminal height and manufacturer limits',
    regionalSpecification:{status:'deferred',applied:false},
    note:'Terminal height and system limits both come from the catalogue record; no engine defaults are used.'
  }},
  evaluateTerminalPlan:function(options){
    options=options||{};
    var b=options.building||null,pt=options.flueCentreMm||null,
        hit=options.roofIntersection||null,
        sys=options.system||catalogue()&&catalogue().getFlueSystem&&catalogue().getFlueSystem(options.flueSystemId||'harvia-whp1500');
    if(!b||!pt||!sys)return{valid:false,errors:[{code:'TERMINAL_PLAN_INPUT_INCOMPLETE',message:'building, flueCentreMm and a selected flue system are required.'}]};
    if(!hit)hit=roofIntersectionVertical(b,pt.x,pt.z);
    if(!hit)return{valid:false,errors:[{code:'ROOF_INTERSECTION_NOT_FOUND',message:'Could not resolve roof intersection for terminal planning.'}]};
    var errs=[],req=flueSystemRequirements(sys,errs);
    if(!req)return{valid:false,errors:errs};
    var outer=num(options.flueOuterDiameterMm,req.outerDiameterMm),
        q=manufacturerTerminalPlan(b,hit,outer,sys,req);q.valid=true;return q;
  },
  evaluateManufacturerTerminal:function(options){
    /* Compatibility API: returns the combined terminal plan. Manufacturer limits
       remain identifiable under manufacturerConstraints; height authority is explicit. */
    return this.evaluateTerminalPlan(options);
  },
  resolveCurrentTerminalValidation:function(options){
    var x=clone(options||{});x.project=currentProject();
    if(requestedFuel(x)==='electric')return{valid:true,status:'not-applicable',reason:'Electric heater requires no terminal.'};
    var r=resolveInstallation(x),t=r&&r.result&&r.result.assembly&&r.result.assembly.flue&&r.result.assembly.flue.terminal;
    return t?clone(t.compliance||t.manufacturerValidation):
      {valid:false,errors:r&&r.errors||[{code:'INSTALLATION_UNRESOLVED',message:'Heater installation could not be resolved.'}]};
  },

  generateBuilding:function(project){return generateBuilding(unwrapProject(project)||project||currentProject())},
  roofIntersectionVertical:roofIntersectionVertical
};

API.memoStats=function(){return{hits:MEMO.hits,misses:MEMO.misses}};
W.ThermoAspenHeaterSystemEngine=API;

try{
  W.dispatchEvent(new CustomEvent('ThermoAspenHeaterSystemEngineReady',{
    detail:{version:VERSION,stage:STAGE,environment:validateEnvironment()}
  }));
}catch(e){}

})(window);
